import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ArrowsClockwise,
  Bank,
  Coins,
  Download,
  Files,
  HandCoins,
  MagnifyingGlass,
  Receipt,
  Users,
  UsersThree,
  Vault,
  Warning,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'
import { exportarExcel, exportarPDF } from '@/lib/exportReport'
import { fetchConfigReporte } from '@/lib/useConfigReporte'
import { haceDiasGT, hoyGT } from '../lib/fechas'

/**
 * NeoCoreReportesPage — reportería consolidada para el gerente.
 *
 * Cada categoría es una vista independiente con filtros propios,
 * timestamps completos (DD/MM/YYYY HH:MM:SS) y export CSV.
 *
 * Categorías disponibles:
 *   - Movimientos     · caja histórica completa con filtros
 *   - Aportaciones    · [placeholder · expandir en siguiente sprint]
 *   - Créditos        · [placeholder · expandir en siguiente sprint]
 *   - Actividad       · [placeholder · expandir en siguiente sprint]
 *
 * URL: /neocore/reportes?cat=movimientos&desde=…&hasta=…
 * El estado de filtros persiste en la URL para poder compartir un
 * reporte específico con un link.
 */

type Categoria = 'movimientos' | 'caja' | 'bancos' | 'aportaciones' | 'creditos' | 'actividad' | 'demografia' | 'cartera'

const CATEGORIAS: {
  id: Categoria
  label: string
  Icon: typeof Receipt
  descripcion: string
  ready: boolean
}[] = [
  {
    id: 'movimientos',
    label: 'Movimientos',
    Icon: Receipt,
    descripcion: 'Histórico completo de caja con filtros por fecha, tipo y asociado',
    ready: true,
  },
  {
    id: 'caja',
    label: 'Caja histórica',
    Icon: Vault,
    descripcion: 'Sesiones de caja con arqueo, diferencial y movimientos',
    ready: true,
  },
  {
    id: 'bancos',
    label: 'Bancos',
    Icon: Bank,
    descripcion: 'Boletas por cuenta bancaria, aplicado vs disponible, conciliación',
    ready: true,
  },
  {
    id: 'aportaciones',
    label: 'Aportaciones',
    Icon: Coins,
    descripcion: 'Capital social por asociado + aportes del período',
    ready: true,
  },
  {
    id: 'creditos',
    label: 'Créditos',
    Icon: HandCoins,
    descripcion: 'Cartera vigente y vencida por rango de mora',
    ready: true,
  },
  {
    id: 'actividad',
    label: 'Nuevos asociados',
    Icon: Users,
    descripcion: 'Altas de asociados en el período seleccionado',
    ready: true,
  },
  {
    id: 'demografia',
    label: 'Demografía',
    Icon: UsersThree,
    descripcion: 'Perfil demográfico de asociados activos: género, edad, residencia, profesión e ingresos',
    ready: true,
  },
  {
    id: 'cartera',
    label: 'Cartera INGECOP',
    Icon: Files,
    descripcion: 'Cartera de ahorro, plazo fijo y aportaciones en formato INGECOP',
    ready: true,
  },
]

export function NeoCoreReportesPage() {
  const [params, setParams] = useSearchParams()
  const cat = (params.get('cat') as Categoria) ?? 'movimientos'

  const setCategoria = (c: Categoria) => {
    const next = new URLSearchParams(window.location.search)
    next.set('cat', c)
    setParams(next)
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20, alignItems: 'start' }}>
      <aside style={{
        background: 'var(--surface)', borderRadius: 12,
        border: '1px solid var(--hairline)', padding: 8,
        position: 'sticky', top: 16,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
          color: 'var(--muted-foreground)', padding: '8px 12px 4px',
        }}>
          Reportes
        </div>
        {CATEGORIAS.map((c) => {
          const active = cat === c.id
          return (
            <button
              key={c.id}
              onClick={() => setCategoria(c.id)}
              disabled={!c.ready}
              style={{
                width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center',
                gap: 10, padding: '10px 12px', borderRadius: 8, border: 'none',
                background: active ? 'var(--brand-50)' : 'transparent',
                color: active ? 'var(--brand)' : c.ready ? 'var(--ink)' : 'var(--muted-foreground)',
                cursor: c.ready ? 'pointer' : 'not-allowed',
                fontSize: 13, fontWeight: active ? 600 : 500,
                marginBottom: 2,
              }}
            >
              <c.Icon size={16} weight={active ? 'fill' : 'regular'} />
              <span style={{ flex: 1 }}>{c.label}</span>
              {!c.ready && (
                <span style={{
                  fontSize: 9, fontWeight: 700, background: 'var(--n-100)',
                  color: 'var(--n-500)', padding: '2px 6px', borderRadius: 999,
                  textTransform: 'uppercase', letterSpacing: 0.4,
                }}>
                  Pronto
                </span>
              )}
            </button>
          )
        })}
      </aside>

      <div style={{ minWidth: 0 }}>
        {cat === 'movimientos' && <MovimientosReporte />}
        {cat === 'caja' && <CajaHistoricaReporte />}
        {cat === 'bancos' && <BancosReporte />}
        {cat === 'aportaciones' && <AportacionesReporte />}
        {cat === 'creditos' && <CreditosReporte />}
        {cat === 'actividad' && <NuevosAsociadosReporte />}
        {cat === 'demografia' && <DemografiaReporte />}
        {cat === 'cartera' && <CarteraIngecopSection />}
      </div>
    </div>
  )
}

// ============================================================================
// Reporte de Movimientos
// ============================================================================

type MovimientoRow = {
  id: string
  correlativo: number
  fecha: string
  monto: number
  descripcion: string | null
  tipo_codigo: string
  tipo_nombre: string
  clase: 'INGRESO' | 'EGRESO'
  serie_alias: string | null
  serie_tipo: string | null
  asociado_id: string | null
  asociado_nombre: string | null
  asociado_neo_id: string | null
  cajero_email: string | null
  anulado: boolean
  anulado_at: string | null
}

// ============================================================================
// Sort utils
// ============================================================================

type SortDir = 'asc' | 'desc'

function SortableHeader<K extends string>({
  label,
  sortKey,
  currentSort,
  currentDir,
  onSort,
  align = 'left',
}: {
  label: string
  sortKey: K
  currentSort: K
  currentDir: SortDir
  onSort: (k: K) => void
  align?: 'left' | 'right'
}) {
  const active = currentSort === sortKey
  return (
    <button
      onClick={() => onSort(sortKey)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: 'transparent', border: 'none', padding: 0,
        cursor: 'pointer', fontSize: 10.5, fontWeight: 700,
        color: active ? 'var(--brand)' : 'var(--n-600)',
        textTransform: 'uppercase', letterSpacing: 0.4,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        width: '100%',
        transition: 'color .12s',
      }}
      onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink)' }}
      onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.color = 'var(--n-600)' }}
    >
      {label}
      {active ? (
        currentDir === 'asc'
          ? <CaretUp size={10} weight="bold" />
          : <CaretDown size={10} weight="bold" />
      ) : (
        <span style={{ opacity: 0.5, display: 'inline-flex' }}>
          <CaretDown size={10} weight="bold" />
        </span>
      )}
    </button>
  )
}

// "Hoy" y rangos SIEMPRE en hora Guatemala (lib/fechas) — la TZ del
// navegador daba la fecha de mañana después de las 18:00 GT.
const todayISO = () => hoyGT()
const daysAgoISO = (days: number) => haceDiasGT(days)

type MovSortKey = 'correlativo' | 'fecha' | 'concepto' | 'monto' | 'asociado'

function MovimientosReporte() {
  const [params, setParams] = useSearchParams()
  const desde = params.get('desde') ?? daysAgoISO(30)
  const hasta = params.get('hasta') ?? todayISO()
  const claseFilter = (params.get('clase') as 'INGRESO' | 'EGRESO' | '') ?? ''
  const search = params.get('q') ?? ''
  const incluirAnulados = params.get('anulados') === '1'
  const sortBy = (params.get('sort') as MovSortKey) ?? 'fecha'
  const sortDir: SortDir = (params.get('dir') as SortDir) ?? 'desc'

  const toggleSort = (key: MovSortKey) => {
    const next = new URLSearchParams(window.location.search)
    if (sortBy === key) {
      next.set('dir', sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      next.set('sort', key)
      // Defaults sensatos: numéricos y fecha → desc; texto → asc
      next.set('dir', key === 'correlativo' || key === 'fecha' || key === 'monto' ? 'desc' : 'asc')
    }
    setParams(next)
  }

  // Detecta cuál preset de rango está activo (Hoy / Ayer / 7d / 30d / Mes)
  const rangoActivo = useMemo<'hoy' | 'ayer' | '7d' | '30d' | 'mes' | 'custom'>(() => {
    if (desde === todayISO() && hasta === todayISO()) return 'hoy'
    if (desde === daysAgoISO(1) && hasta === daysAgoISO(1)) return 'ayer'
    if (desde === daysAgoISO(7) && hasta === todayISO()) return '7d'
    if (desde === daysAgoISO(30) && hasta === todayISO()) return '30d'
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    if (desde === mesDesde && hasta === todayISO()) return 'mes'
    return 'custom'
  }, [desde, hasta])

  const hayFiltrosActivos = claseFilter !== '' || search !== '' || incluirAnulados

  const [rows, setRows] = useState<MovimientoRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from('movimiento_caja')
        .select(`
          id, correlativo, fecha, created_at, monto, descripcion,
          anulado, anulado_at,
          tipo_movimiento:tipo_movimiento_caja!inner (codigo, nombre, clase),
          serie:serie_documento_id (alias, tipo_documento),
          asociado:asociado_id (id, neo_id, nombre_completo)
        `)
        // Rango en hora de Guatemala (UTC-6) para que "Hoy" no traiga otro día.
        .gte('created_at', `${desde}T00:00:00-06:00`)
        .lte('created_at', `${hasta}T23:59:59.999-06:00`)
        .order('created_at', { ascending: false })
        .limit(500)

      if (!incluirAnulados) {
        query = query.eq('anulado', false)
      }

      const { data, error: err } = await query
      if (err) throw err

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped: MovimientoRow[] = (data ?? []).map((r: any) => ({
        id: r.id,
        correlativo: r.correlativo ?? 0,
        // Preferimos created_at (timestamptz con hora real) sobre fecha (date sin hora).
        // Movimientos migrados de COBASY tienen created_at = fecha migración → sin hora útil.
        // Movimientos nuevos NeoCore/app → created_at con hora real GT.
        fecha: r.created_at ?? r.fecha,
        monto: Number(r.monto ?? 0),
        descripcion: r.descripcion,
        tipo_codigo: r.tipo_movimiento?.codigo ?? '',
        tipo_nombre: r.tipo_movimiento?.nombre ?? '',
        clase: r.tipo_movimiento?.clase ?? 'INGRESO',
        serie_alias: r.serie?.alias ?? null,
        serie_tipo: r.serie?.tipo_documento ?? null,
        asociado_id: r.asociado?.id ?? null,
        asociado_nombre: r.asociado?.nombre_completo ?? null,
        asociado_neo_id: r.asociado?.neo_id ?? null,
        cajero_email: null,
        anulado: r.anulado ?? false,
        anulado_at: r.anulado_at,
      }))

      const q = search.trim().toLowerCase()
      const filtered = mapped.filter((m) => {
        if (claseFilter && m.clase !== claseFilter) return false
        if (q) {
          const matches =
            String(m.correlativo).includes(q) ||
            m.asociado_nombre?.toLowerCase().includes(q) ||
            m.asociado_neo_id?.includes(q) ||
            m.descripcion?.toLowerCase().includes(q) ||
            m.tipo_nombre?.toLowerCase().includes(q)
          if (!matches) return false
        }
        return true
      })

      setRows(filtered)
    } catch (e: unknown) {
      // Supabase error tiene shape distinto a Error nativo.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any
      const msg = err?.message ?? err?.details ?? err?.hint ?? JSON.stringify(err) ?? 'Error desconocido'
      setError(msg)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [desde, hasta, claseFilter, search, incluirAnulados])

  useEffect(() => { void load() }, [load])

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    const cmp = (a: MovimientoRow, b: MovimientoRow) => {
      switch (sortBy) {
        case 'correlativo':
          return (a.correlativo - b.correlativo) * dir
        case 'fecha':
          return (new Date(a.fecha).getTime() - new Date(b.fecha).getTime()) * dir
        case 'monto':
          return (a.monto - b.monto) * dir
        case 'concepto':
          return (a.tipo_nombre ?? a.descripcion ?? '').localeCompare(b.tipo_nombre ?? b.descripcion ?? '', 'es') * dir
        case 'asociado':
          return (a.asociado_nombre ?? '').localeCompare(b.asociado_nombre ?? '', 'es') * dir
        default:
          return 0
      }
    }
    return [...rows].sort(cmp)
  }, [rows, sortBy, sortDir])

  const setFiltro = (key: string, val: string) => {
    const next = new URLSearchParams(window.location.search)
    if (val) next.set(key, val)
    else next.delete(key)
    setParams(next)
  }

  const setRango = (preset: 'hoy' | 'ayer' | '7d' | '30d' | 'mes') => {
    const next = new URLSearchParams(window.location.search)
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    const rangos = {
      hoy: { desde: todayISO(), hasta: todayISO() },
      ayer: { desde: daysAgoISO(1), hasta: daysAgoISO(1) },
      '7d': { desde: daysAgoISO(7), hasta: todayISO() },
      '30d': { desde: daysAgoISO(30), hasta: todayISO() },
      mes: { desde: mesDesde, hasta: todayISO() },
    }
    next.set('desde', rangos[preset].desde)
    next.set('hasta', rangos[preset].hasta)
    setParams(next)
  }

  const limpiarFiltros = () => {
    const next = new URLSearchParams(window.location.search)
    next.delete('clase')
    next.delete('q')
    next.delete('anulados')
    setParams(next)
  }

  const kpis = useMemo(() => {
    const activos = rows.filter((r) => !r.anulado)
    const ingresos = activos.filter((r) => r.clase === 'INGRESO').reduce((a, r) => a + r.monto, 0)
    const egresos = activos.filter((r) => r.clase === 'EGRESO').reduce((a, r) => a + r.monto, 0)
    const anulados = rows.filter((r) => r.anulado).length
    return { count: rows.length, ingresos, egresos, neto: ingresos - egresos, anulados }
  }, [rows])

  const exportCSV = () => {
    const header = [
      'correlativo', 'serie', 'fecha', 'hora',
      'tipo_codigo', 'tipo_nombre', 'clase',
      'asociado_neo_id', 'asociado_nombre',
      'monto', 'descripcion', 'cajero', 'anulado',
    ]
    const escape = (v: unknown): string => {
      if (v === null || v === undefined) return ''
      const s = String(v).replace(/"/g, '""')
      return /[",\n]/.test(s) ? `"${s}"` : s
    }
    const lines = [header.join(',')]
    for (const r of sortedRows) {
      const d = new Date(r.fecha)
      const fecha = d.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala' })
      const hora = d.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false })
      const serie = r.serie_alias ? `${r.serie_alias}-${String(r.correlativo).padStart(4, '0')}` : String(r.correlativo)
      lines.push([
        r.correlativo, serie, fecha, hora,
        r.tipo_codigo, r.tipo_nombre, r.clase,
        r.asociado_neo_id ?? '', r.asociado_nombre ?? '',
        r.monto.toFixed(2), r.descripcion ?? '',
        r.cajero_email ?? '', r.anulado ? '1' : '0',
      ].map(escape).join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `movimientos_${desde}_a_${hasta}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Reporte
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>
            Movimientos históricos
          </h2>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
            {loading ? 'Cargando…' : `${rows.length} ${rows.length === 1 ? 'movimiento' : 'movimientos'} · máx. 500`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={load}
            className="btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <ArrowsClockwise size={14} />
            Refrescar
          </button>
          <button
            onClick={exportCSV}
            disabled={rows.length === 0}
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <Download size={14} />
            Exportar CSV
          </button>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
      }}>
        <KpiTile label="Movimientos" value={String(kpis.count)} />
        <KpiTile label="Ingresos" value={formatQ(kpis.ingresos)} tone="success" />
        <KpiTile label="Egresos" value={formatQ(kpis.egresos)} tone="danger" />
        <KpiTile label="Neto" value={formatQ(kpis.neto)} tone={kpis.neto >= 0 ? 'success' : 'danger'} />
      </div>

      <div style={{
        padding: '14px 16px', background: 'var(--surface)',
        border: '1px solid var(--hairline)', borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['hoy', 'ayer', '7d', '30d', 'mes'] as const).map((preset) => {
            const labels = { hoy: 'Hoy', ayer: 'Ayer', '7d': 'Últimos 7 días', '30d': 'Últimos 30 días', mes: 'Este mes' }
            const active = rangoActivo === preset
            return (
              <button
                key={preset}
                onClick={() => setRango(preset)}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  border: `1px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
                  background: active ? 'var(--brand)' : 'transparent',
                  color: active ? 'white' : 'var(--ink)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                {labels[preset]}
              </button>
            )
          })}
          <div style={{
            padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${rangoActivo === 'custom' ? 'var(--brand)' : 'var(--hairline)'}`,
            background: rangoActivo === 'custom' ? 'rgba(240,78,35,0.08)' : 'var(--soft-gray)',
            fontSize: 12, color: rangoActivo === 'custom' ? 'var(--brand)' : 'var(--muted-foreground)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ fontWeight: 600 }}>Rango</span>
            <input
              type="date"
              value={desde}
              onChange={(e) => setFiltro('desde', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }}
            />
            <span>→</span>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setFiltro('hasta', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
            <MagnifyingGlass
              size={14}
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)' }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setFiltro('q', e.target.value)}
              placeholder="Buscar correlativo, asociado, descripción o tipo…"
              style={{ fontSize: 13, height: 34, padding: '0 12px 0 32px', width: '100%' }}
            />
          </div>
          <div style={{ display: 'inline-flex', background: 'var(--soft-gray)', borderRadius: 8, padding: 2, gap: 2 }}>
            {[
              { v: '', label: 'Todos' },
              { v: 'INGRESO', label: 'Ingresos' },
              { v: 'EGRESO', label: 'Egresos' },
            ].map((opt) => {
              const active = claseFilter === opt.v
              return (
                <button
                  key={opt.v}
                  onClick={() => setFiltro('clase', opt.v)}
                  style={{
                    padding: '6px 12px', borderRadius: 6, border: 'none',
                    background: active ? 'var(--surface)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--muted-foreground)',
                    fontSize: 12, fontWeight: active ? 600 : 500, cursor: 'pointer',
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
            color: incluirAnulados ? 'var(--ink)' : 'var(--muted-foreground)',
            padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${incluirAnulados ? 'var(--hairline)' : 'transparent'}`,
            background: incluirAnulados ? 'var(--soft-gray)' : 'transparent',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={incluirAnulados}
              onChange={(e) => setFiltro('anulados', e.target.checked ? '1' : '')}
              style={{ margin: 0 }}
            />
            Incluir anulados
          </label>
          {hayFiltrosActivos && (
            <button
              onClick={limpiarFiltros}
              style={{
                padding: '6px 12px', borderRadius: 999, border: '1px solid var(--hairline)',
                background: 'transparent', color: 'var(--brand)', fontSize: 12, fontWeight: 500,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning size={16} weight="fill" />
          {error}
        </div>
      )}

      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '90px 130px 1fr 130px 200px 40px',
          gap: 12, padding: '10px 16px',
          borderBottom: '1px solid var(--hairline)', background: 'var(--soft-gray)',
        }}>
          <SortableHeader label="Serie / #" sortKey="correlativo" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Fecha y hora" sortKey="fecha" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Concepto" sortKey="concepto" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Monto" sortKey="monto" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Asociado" sortKey="asociado" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <div></div>
        </div>

        {loading && sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            Sin movimientos en este rango.
          </div>
        ) : (
          sortedRows.map((r) => {
            const d = new Date(r.fecha)
            const isExpanded = expanded === r.id
            // Si el timestamp original es midnight UTC exacto, es un
            // movimiento migrado sin hora real · no mostrar hora falsa.
            const soloFecha = r.fecha.endsWith('T00:00:00+00:00') ||
              r.fecha.endsWith('T00:00:00.000+00:00') ||
              /T00:00:00(\.0+)?\+00:00$/.test(r.fecha) ||
              /T00:00:00(\.0+)?Z$/.test(r.fecha) ||
              /^\d{4}-\d{2}-\d{2}$/.test(r.fecha)
            return (
              <div key={r.id}>
                <div
                  onClick={() => setExpanded(isExpanded ? null : r.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '90px 130px 1fr 130px 200px 40px',
                    gap: 12, padding: '12px 16px',
                    fontSize: 13, alignItems: 'center', cursor: 'pointer',
                    borderBottom: isExpanded ? 'none' : '1px solid var(--hairline)',
                    opacity: r.anulado ? 0.5 : 1,
                    background: isExpanded ? 'var(--soft-gray)' : 'transparent',
                  }}
                >
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--muted-foreground)' }}>
                    {r.serie_alias ? `${r.serie_alias}-${String(r.correlativo).padStart(4, '0')}` : `#${r.correlativo}`}
                  </div>
                  <div>
                    <div style={{ fontSize: 12 }}>
                      {d.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala', day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                    {soloFecha ? (
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', fontStyle: 'italic' }}>
                        sin hora
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', fontFamily: 'monospace' }}>
                        {d.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false })}
                      </div>
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      textDecoration: r.anulado ? 'line-through' : 'none',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {r.descripcion ?? r.tipo_nombre}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                      {r.tipo_codigo} · {r.tipo_nombre}
                      {r.anulado && ' · ANULADO'}
                    </div>
                  </div>
                  <div style={{
                    textAlign: 'right', fontWeight: 600,
                    color: r.anulado ? 'var(--muted-foreground)' : r.clase === 'INGRESO' ? 'var(--positive)' : 'var(--brand)',
                    fontVariantNumeric: 'tabular-nums',
                    textDecoration: r.anulado ? 'line-through' : 'none',
                  }}>
                    {r.clase === 'INGRESO' ? '+' : '−'}{formatQ(r.monto)}
                  </div>
                  <div style={{ fontSize: 12, minWidth: 0 }}>
                    {r.asociado_nombre ? (
                      <>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.asociado_nombre}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                          {r.asociado_neo_id}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: 'var(--muted-foreground)' }}>—</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'center', color: 'var(--muted-foreground)' }}>
                    {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                  </div>
                </div>
                {isExpanded && (
                  <div style={{
                    padding: '10px 16px 14px 106px', background: 'var(--soft-gray)',
                    fontSize: 12, color: 'var(--ink-2)',
                    borderBottom: '1px solid var(--hairline)',
                    display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px',
                  }}>
                    <div style={{ color: 'var(--muted-foreground)' }}>Fecha y hora completa:</div>
                    <div style={{ fontFamily: 'monospace' }}>
                      {d.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala', weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                      {' · '}
                      {d.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false })}
                      {' GT'}
                    </div>
                    <div style={{ color: 'var(--muted-foreground)' }}>Cajero:</div>
                    <div>{r.cajero_email ?? '—'}</div>
                    {r.anulado && (
                      <>
                        <div style={{ color: 'var(--muted-foreground)' }}>Anulado el:</div>
                        <div style={{ color: 'var(--brand)' }}>
                          {r.anulado_at
                            ? new Date(r.anulado_at).toLocaleString('es-GT', { timeZone: 'America/Guatemala', hour12: false })
                            : '—'}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Helpers
// ============================================================================

function KpiTile({ label, value, tone }: { label: string; value: string; tone?: 'success' | 'danger' }) {
  const color = tone === 'success' ? 'var(--positive)' : tone === 'danger' ? 'var(--brand)' : 'var(--ink)'
  return (
    <div style={{
      padding: '10px 14px', background: 'var(--surface)',
      border: '1px solid var(--hairline)', borderRadius: 10,
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--n-600)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{
        fontSize: 18, fontWeight: 700, color, marginTop: 2,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
    </div>
  )
}

// ============================================================================
// Reporte de Nuevos Asociados
// ============================================================================

type AsociadoRow = {
  id: string
  neo_id: string | null
  no_asociado: string | null
  nombre_completo: string
  dpi: string | null
  telefono_e164: string | null
  email_app: string | null
  estado_asociado: string
  fecha_ingreso: string | null
  created_at: string
}

const ESTADOS_LABEL: Record<string, string> = {
  prospecto: 'Prospecto',
  pendiente: 'Pendiente',
  activo: 'Activo',
  suspendido: 'Suspendido',
  retirado: 'Retirado',
}

const ESTADOS_TONE: Record<string, string> = {
  activo:     'rgba(15,138,87,0.12)',
  prospecto:  'rgba(184,134,11,0.12)',
  pendiente:  'rgba(184,134,11,0.12)',
  suspendido: 'rgba(220,38,38,0.10)',
  retirado:   'rgba(100,100,100,0.10)',
}

const ESTADOS_COLOR: Record<string, string> = {
  activo:     '#0F8A57',
  prospecto:  '#B8860B',
  pendiente:  '#B8860B',
  suspendido: '#B91C1C',
  retirado:   '#666',
}

type AsocSortKey = 'fecha' | 'neo_id' | 'nombre' | 'telefono' | 'email' | 'estado'

function NuevosAsociadosReporte() {
  const [params, setParams] = useSearchParams()
  const desde = params.get('desde') ?? daysAgoISO(30)
  const hasta = params.get('hasta') ?? todayISO()
  const estadoFilter = params.get('estado') ?? ''
  const search = params.get('q') ?? ''
  const sortBy = (params.get('sort') as AsocSortKey) ?? 'fecha'
  const sortDir: SortDir = (params.get('dir') as SortDir) ?? 'desc'

  const toggleSort = (key: AsocSortKey) => {
    const next = new URLSearchParams(window.location.search)
    if (sortBy === key) {
      next.set('dir', sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      next.set('sort', key)
      next.set('dir', key === 'fecha' ? 'desc' : 'asc')
    }
    setParams(next)
  }

  const [rows, setRows] = useState<AsociadoRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const rangoActivo = useMemo<'hoy' | 'ayer' | '7d' | '30d' | 'mes' | 'custom'>(() => {
    if (desde === todayISO() && hasta === todayISO()) return 'hoy'
    if (desde === daysAgoISO(1) && hasta === daysAgoISO(1)) return 'ayer'
    if (desde === daysAgoISO(7) && hasta === todayISO()) return '7d'
    if (desde === daysAgoISO(30) && hasta === todayISO()) return '30d'
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    if (desde === mesDesde && hasta === todayISO()) return 'mes'
    return 'custom'
  }, [desde, hasta])

  const setFiltro = (key: string, val: string) => {
    const next = new URLSearchParams(window.location.search)
    if (val) next.set(key, val)
    else next.delete(key)
    setParams(next)
  }

  const setRango = (preset: 'hoy' | 'ayer' | '7d' | '30d' | 'mes') => {
    const next = new URLSearchParams(window.location.search)
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    const rangos = {
      hoy: { desde: todayISO(), hasta: todayISO() },
      ayer: { desde: daysAgoISO(1), hasta: daysAgoISO(1) },
      '7d': { desde: daysAgoISO(7), hasta: todayISO() },
      '30d': { desde: daysAgoISO(30), hasta: todayISO() },
      mes: { desde: mesDesde, hasta: todayISO() },
    }
    next.set('desde', rangos[preset].desde)
    next.set('hasta', rangos[preset].hasta)
    setParams(next)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await supabase
        .from('asociado')
        .select('id, neo_id, no_asociado, nombre_completo, dpi, telefono_e164, email_app, estado_asociado, fecha_ingreso, created_at')
        .gte('created_at', `${desde}T00:00:00-06:00`)
        .lte('created_at', `${hasta}T23:59:59.999-06:00`)
        .order('created_at', { ascending: false })
        .limit(500)
      if (err) throw err

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped: AsociadoRow[] = (data ?? []).map((r: any) => ({
        id: r.id,
        neo_id: r.neo_id,
        no_asociado: r.no_asociado,
        nombre_completo: r.nombre_completo ?? '',
        dpi: r.dpi,
        telefono_e164: r.telefono_e164,
        email_app: r.email_app,
        estado_asociado: r.estado_asociado ?? 'prospecto',
        fecha_ingreso: r.fecha_ingreso,
        created_at: r.created_at,
      }))

      const q = search.trim().toLowerCase()
      const filtered = mapped.filter((a) => {
        if (estadoFilter && a.estado_asociado !== estadoFilter) return false
        if (q) {
          const matches =
            a.nombre_completo?.toLowerCase().includes(q) ||
            a.neo_id?.includes(q) ||
            a.dpi?.includes(q) ||
            a.telefono_e164?.includes(q) ||
            a.email_app?.toLowerCase().includes(q)
          if (!matches) return false
        }
        return true
      })

      setRows(filtered)
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any
      const msg = err?.message ?? err?.details ?? err?.hint ?? JSON.stringify(err) ?? 'Error desconocido'
      setError(msg)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [desde, hasta, estadoFilter, search])

  useEffect(() => { void load() }, [load])

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    const cmp = (a: AsociadoRow, b: AsociadoRow) => {
      switch (sortBy) {
        case 'fecha':
          return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * dir
        case 'neo_id':
          return (a.neo_id ?? '').localeCompare(b.neo_id ?? '', 'es', { numeric: true }) * dir
        case 'nombre':
          return a.nombre_completo.localeCompare(b.nombre_completo, 'es') * dir
        case 'telefono':
          return (a.telefono_e164 ?? '').localeCompare(b.telefono_e164 ?? '', 'es') * dir
        case 'email':
          return (a.email_app ?? '').localeCompare(b.email_app ?? '', 'es') * dir
        case 'estado':
          return (a.estado_asociado ?? '').localeCompare(b.estado_asociado ?? '', 'es') * dir
        default:
          return 0
      }
    }
    return [...rows].sort(cmp)
  }, [rows, sortBy, sortDir])

  const kpis = useMemo(() => {
    const activos = rows.filter((r) => r.estado_asociado === 'activo').length
    const prospectos = rows.filter((r) => r.estado_asociado === 'prospecto').length
    const pendientes = rows.filter((r) => r.estado_asociado === 'pendiente').length
    return { total: rows.length, activos, prospectos, pendientes }
  }, [rows])

  const exportCSV = () => {
    const header = ['fecha_alta', 'hora_alta', 'neo_id', 'nombre', 'dpi', 'telefono', 'email', 'estado', 'fecha_ingreso']
    const escape = (v: unknown): string => {
      if (v === null || v === undefined) return ''
      const s = String(v).replace(/"/g, '""')
      return /[",\n]/.test(s) ? `"${s}"` : s
    }
    const lines = [header.join(',')]
    for (const r of sortedRows) {
      const d = new Date(r.created_at)
      const fecha = d.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala' })
      const hora = d.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false })
      lines.push([
        fecha, hora, r.neo_id ?? '', r.nombre_completo, r.dpi ?? '',
        r.telefono_e164 ?? '', r.email_app ?? '', r.estado_asociado,
        r.fecha_ingreso ?? '',
      ].map(escape).join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `nuevos_asociados_${desde}_a_${hasta}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const limpiarFiltros = () => {
    const next = new URLSearchParams(window.location.search)
    next.delete('estado')
    next.delete('q')
    setParams(next)
  }

  const hayFiltrosActivos = estadoFilter !== '' || search !== ''

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Reporte
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>
            Nuevos asociados
          </h2>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
            {loading ? 'Cargando…' : `${rows.length} ${rows.length === 1 ? 'asociado dado de alta' : 'asociados dados de alta'}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <ArrowsClockwise size={14} />
            Refrescar
          </button>
          <button onClick={exportCSV} disabled={rows.length === 0} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <Download size={14} />
            Exportar CSV
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <KpiTile label="Nuevos" value={String(kpis.total)} />
        <KpiTile label="Activos" value={String(kpis.activos)} tone="success" />
        <KpiTile label="Prospectos" value={String(kpis.prospectos)} />
        <KpiTile label="Pendientes" value={String(kpis.pendientes)} />
      </div>

      <div style={{
        padding: '14px 16px', background: 'var(--surface)',
        border: '1px solid var(--hairline)', borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['hoy', 'ayer', '7d', '30d', 'mes'] as const).map((preset) => {
            const labels = { hoy: 'Hoy', ayer: 'Ayer', '7d': 'Últimos 7 días', '30d': 'Últimos 30 días', mes: 'Este mes' }
            const active = rangoActivo === preset
            return (
              <button
                key={preset}
                onClick={() => setRango(preset)}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  border: `1px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
                  background: active ? 'var(--brand)' : 'transparent',
                  color: active ? 'white' : 'var(--ink)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                {labels[preset]}
              </button>
            )
          })}
          <div style={{
            padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${rangoActivo === 'custom' ? 'var(--brand)' : 'var(--hairline)'}`,
            background: rangoActivo === 'custom' ? 'rgba(240,78,35,0.08)' : 'var(--soft-gray)',
            fontSize: 12, color: rangoActivo === 'custom' ? 'var(--brand)' : 'var(--muted-foreground)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ fontWeight: 600 }}>Rango</span>
            <input type="date" value={desde} onChange={(e) => setFiltro('desde', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
            <span>→</span>
            <input type="date" value={hasta} onChange={(e) => setFiltro('hasta', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
            <MagnifyingGlass size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)' }} />
            <input type="text" value={search} onChange={(e) => setFiltro('q', e.target.value)}
              placeholder="Buscar nombre, neo_id, DPI, teléfono o email…"
              style={{ fontSize: 13, height: 34, padding: '0 12px 0 32px', width: '100%' }} />
          </div>
          <div style={{ display: 'inline-flex', background: 'var(--soft-gray)', borderRadius: 8, padding: 2, gap: 2 }}>
            {[
              { v: '', label: 'Todos' },
              { v: 'activo', label: 'Activos' },
              { v: 'prospecto', label: 'Prospectos' },
              { v: 'pendiente', label: 'Pendientes' },
            ].map((opt) => {
              const active = estadoFilter === opt.v
              return (
                <button key={opt.v} onClick={() => setFiltro('estado', opt.v)}
                  style={{
                    padding: '6px 12px', borderRadius: 6, border: 'none',
                    background: active ? 'var(--surface)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--muted-foreground)',
                    fontSize: 12, fontWeight: active ? 600 : 500, cursor: 'pointer',
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}>
                  {opt.label}
                </button>
              )
            })}
          </div>
          {hayFiltrosActivos && (
            <button onClick={limpiarFiltros}
              style={{
                padding: '6px 12px', borderRadius: 999, border: '1px solid var(--hairline)',
                background: 'transparent', color: 'var(--brand)', fontSize: 12, fontWeight: 500,
                cursor: 'pointer',
              }}>
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning size={16} weight="fill" />
          {error}
        </div>
      )}

      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '130px 90px 1fr 160px 130px 100px',
          gap: 12, padding: '10px 16px',
          borderBottom: '1px solid var(--hairline)', background: 'var(--soft-gray)',
        }}>
          <SortableHeader label="Fecha de alta" sortKey="fecha" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Neo.id" sortKey="neo_id" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Nombre" sortKey="nombre" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Teléfono" sortKey="telefono" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Email" sortKey="email" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Estado" sortKey="estado" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
        </div>

        {loading && sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            Sin nuevos asociados en este rango.
          </div>
        ) : (
          sortedRows.map((a) => {
            const d = new Date(a.created_at)
            return (
              <div key={a.id} style={{
                display: 'grid',
                gridTemplateColumns: '130px 90px 1fr 160px 130px 100px',
                gap: 12, padding: '12px 16px',
                fontSize: 13, alignItems: 'center',
                borderBottom: '1px solid var(--hairline)',
              }}>
                <div>
                  <div style={{ fontSize: 12 }}>
                    {d.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala', day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)', fontFamily: 'monospace' }}>
                    {d.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false })}
                  </div>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink)' }}>
                  {a.neo_id ?? '—'}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.nombre_completo}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                    {a.dpi ? `DPI ${a.dpi}` : '—'}
                  </div>
                </div>
                <div style={{ fontSize: 12, fontFamily: 'monospace' }}>
                  {a.telefono_e164 ?? '—'}
                </div>
                <div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.email_app ?? '—'}
                </div>
                <div>
                  <span style={{
                    padding: '3px 8px', borderRadius: 999,
                    background: ESTADOS_TONE[a.estado_asociado] ?? 'var(--soft-gray)',
                    color: ESTADOS_COLOR[a.estado_asociado] ?? 'var(--ink)',
                    fontSize: 11, fontWeight: 600,
                    display: 'inline-block',
                  }}>
                    {ESTADOS_LABEL[a.estado_asociado] ?? a.estado_asociado}
                  </span>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Reporte de Aportaciones
// ============================================================================

type AportacionRow = {
  cuenta_id: string
  numero_cuenta: string
  asociado_id: string
  neo_id: string | null
  nombre_completo: string
  estado_asociado: string
  saldo_actual: number
  aportado_periodo: number
  movs_periodo: number
  ultima_aportacion: string | null
  abierta_at: string
}

type AportSortKey = 'saldo' | 'aportado' | 'nombre' | 'neo_id' | 'ultima' | 'movs'

function AportacionesReporte() {
  const [params, setParams] = useSearchParams()
  const desde = params.get('desde') ?? daysAgoISO(30)
  const hasta = params.get('hasta') ?? todayISO()
  const search = params.get('q') ?? ''
  const sortBy = (params.get('sort') as AportSortKey) ?? 'saldo'
  const sortDir: SortDir = (params.get('dir') as SortDir) ?? 'desc'

  const rangoActivo = useMemo<'hoy' | 'ayer' | '7d' | '30d' | 'mes' | 'custom'>(() => {
    if (desde === todayISO() && hasta === todayISO()) return 'hoy'
    if (desde === daysAgoISO(1) && hasta === daysAgoISO(1)) return 'ayer'
    if (desde === daysAgoISO(7) && hasta === todayISO()) return '7d'
    if (desde === daysAgoISO(30) && hasta === todayISO()) return '30d'
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    if (desde === mesDesde && hasta === todayISO()) return 'mes'
    return 'custom'
  }, [desde, hasta])

  const setFiltro = (key: string, val: string) => {
    const next = new URLSearchParams(window.location.search)
    if (val) next.set(key, val)
    else next.delete(key)
    setParams(next)
  }

  const setRango = (preset: 'hoy' | 'ayer' | '7d' | '30d' | 'mes') => {
    const next = new URLSearchParams(window.location.search)
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    const rangos = {
      hoy: { desde: todayISO(), hasta: todayISO() },
      ayer: { desde: daysAgoISO(1), hasta: daysAgoISO(1) },
      '7d': { desde: daysAgoISO(7), hasta: todayISO() },
      '30d': { desde: daysAgoISO(30), hasta: todayISO() },
      mes: { desde: mesDesde, hasta: todayISO() },
    }
    next.set('desde', rangos[preset].desde)
    next.set('hasta', rangos[preset].hasta)
    setParams(next)
  }

  const toggleSort = (key: AportSortKey) => {
    const next = new URLSearchParams(window.location.search)
    if (sortBy === key) {
      next.set('dir', sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      next.set('sort', key)
      next.set('dir', key === 'nombre' || key === 'neo_id' ? 'asc' : 'desc')
    }
    setParams(next)
  }

  const [rows, setRows] = useState<AportacionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // 1) Cuentas de aportaciones activas + asociado
      const { data: cuentasData, error: err1 } = await supabase
        .from('cuenta_aportaciones')
        .select(`
          id, numero_cuenta, saldo, abierta_at,
          asociado:asociado_id (id, neo_id, nombre_completo, estado_asociado)
        `)
        .eq('estado', 'activa')
        .limit(2000)
      if (err1) throw err1

      // 2) Movimientos de aportaciones en el rango (ingresos solamente para "aportado")
      const { data: movsData, error: err2 } = await supabase
        .from('movimiento_cuenta')
        .select('cta_aportaciones_id, monto, created_at')
        .not('cta_aportaciones_id', 'is', null)
        .gte('created_at', `${desde}T00:00:00-06:00`)
        .lte('created_at', `${hasta}T23:59:59.999-06:00`)
        .limit(5000)
      if (err2) throw err2

      // 3) Última aportación por cuenta (global, no restringida al rango)
      const { data: ultimasData, error: err3 } = await supabase
        .from('movimiento_cuenta')
        .select('cta_aportaciones_id, created_at')
        .not('cta_aportaciones_id', 'is', null)
        .gt('monto', 0)
        .order('created_at', { ascending: false })
        .limit(3000)
      if (err3) throw err3

      // Agregar movs por cuenta_id
      const agg: Record<string, { total: number; count: number }> = {}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const m of (movsData ?? []) as any[]) {
        const cid = m.cta_aportaciones_id as string
        const monto = Number(m.monto ?? 0)
        if (!agg[cid]) agg[cid] = { total: 0, count: 0 }
        // Solo sumar ingresos (positivos) al "aportado"
        if (monto > 0) agg[cid].total += monto
        agg[cid].count += 1
      }

      const ultimaPorCuenta: Record<string, string> = {}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const m of (ultimasData ?? []) as any[]) {
        const cid = m.cta_aportaciones_id as string
        if (!ultimaPorCuenta[cid]) ultimaPorCuenta[cid] = m.created_at
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped: AportacionRow[] = ((cuentasData ?? []) as any[]).map((c) => {
        const asociado = c.asociado ?? {}
        const cid = c.id as string
        return {
          cuenta_id: cid,
          numero_cuenta: c.numero_cuenta ?? '',
          asociado_id: asociado.id ?? '',
          neo_id: asociado.neo_id ?? null,
          nombre_completo: asociado.nombre_completo ?? '',
          estado_asociado: asociado.estado_asociado ?? '',
          saldo_actual: Number(c.saldo ?? 0),
          aportado_periodo: agg[cid]?.total ?? 0,
          movs_periodo: agg[cid]?.count ?? 0,
          ultima_aportacion: ultimaPorCuenta[cid] ?? null,
          abierta_at: c.abierta_at ?? '',
        }
      })

      // Filtro cliente por search
      const q = search.trim().toLowerCase()
      const filtered = q
        ? mapped.filter(
            (r) =>
              r.nombre_completo.toLowerCase().includes(q) ||
              r.neo_id?.includes(q) ||
              r.numero_cuenta.includes(q),
          )
        : mapped

      setRows(filtered)
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any
      const msg = err?.message ?? err?.details ?? err?.hint ?? JSON.stringify(err) ?? 'Error desconocido'
      setError(msg)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [desde, hasta, search])

  useEffect(() => { void load() }, [load])

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    const cmp = (a: AportacionRow, b: AportacionRow) => {
      switch (sortBy) {
        case 'saldo':    return (a.saldo_actual - b.saldo_actual) * dir
        case 'aportado': return (a.aportado_periodo - b.aportado_periodo) * dir
        case 'movs':     return (a.movs_periodo - b.movs_periodo) * dir
        case 'nombre':   return a.nombre_completo.localeCompare(b.nombre_completo, 'es') * dir
        case 'neo_id':   return (a.neo_id ?? '').localeCompare(b.neo_id ?? '', 'es', { numeric: true }) * dir
        case 'ultima':
          return (
            (new Date(a.ultima_aportacion ?? 0).getTime() - new Date(b.ultima_aportacion ?? 0).getTime()) * dir
          )
        default: return 0
      }
    }
    return [...rows].sort(cmp)
  }, [rows, sortBy, sortDir])

  const kpis = useMemo(() => {
    const capital = rows.reduce((s, r) => s + r.saldo_actual, 0)
    const aportadoPeriodo = rows.reduce((s, r) => s + r.aportado_periodo, 0)
    const conAportacion = rows.filter((r) => r.saldo_actual > 0).length
    const aportaronEnPeriodo = rows.filter((r) => r.aportado_periodo > 0).length
    const promedio = conAportacion > 0 ? capital / conAportacion : 0
    return { capital, aportadoPeriodo, conAportacion, aportaronEnPeriodo, promedio }
  }, [rows])

  const exportCSV = () => {
    const header = [
      'neo_id', 'asociado', 'numero_cuenta', 'estado',
      'saldo_actual', 'aportado_periodo', 'movs_periodo',
      'ultima_aportacion_fecha', 'ultima_aportacion_hora',
    ]
    const escape = (v: unknown): string => {
      if (v === null || v === undefined) return ''
      const s = String(v).replace(/"/g, '""')
      return /[",\n]/.test(s) ? `"${s}"` : s
    }
    const lines = [header.join(',')]
    for (const r of sortedRows) {
      const ua = r.ultima_aportacion ? new Date(r.ultima_aportacion) : null
      const uaFecha = ua ? ua.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala' }) : ''
      const uaHora = ua ? ua.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false }) : ''
      lines.push([
        r.neo_id ?? '', r.nombre_completo, r.numero_cuenta, r.estado_asociado,
        r.saldo_actual.toFixed(2), r.aportado_periodo.toFixed(2), r.movs_periodo,
        uaFecha, uaHora,
      ].map(escape).join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `aportaciones_${desde}_a_${hasta}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Reporte
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>
            Aportaciones
          </h2>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
            {loading ? 'Cargando…' : `${rows.length} cuentas · Q${kpis.capital.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} en capital social`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <ArrowsClockwise size={14} />
            Refrescar
          </button>
          <button onClick={exportCSV} disabled={rows.length === 0} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <Download size={14} />
            Exportar CSV
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <KpiTile label="Capital social" value={formatQ(kpis.capital)} tone="success" />
        <KpiTile label={`Aportado (${rangoActivo === 'hoy' ? 'hoy' : rangoActivo === 'mes' ? 'este mes' : 'período'})`} value={formatQ(kpis.aportadoPeriodo)} />
        <KpiTile label="Aportaron en período" value={`${kpis.aportaronEnPeriodo} / ${rows.length}`} />
        <KpiTile label="Aportación promedio" value={formatQ(kpis.promedio)} />
      </div>

      <div style={{
        padding: '14px 16px', background: 'var(--surface)',
        border: '1px solid var(--hairline)', borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['hoy', 'ayer', '7d', '30d', 'mes'] as const).map((preset) => {
            const labels = { hoy: 'Hoy', ayer: 'Ayer', '7d': 'Últimos 7 días', '30d': 'Últimos 30 días', mes: 'Este mes' }
            const active = rangoActivo === preset
            return (
              <button
                key={preset}
                onClick={() => setRango(preset)}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  border: `1px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
                  background: active ? 'var(--brand)' : 'transparent',
                  color: active ? 'white' : 'var(--ink)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                {labels[preset]}
              </button>
            )
          })}
          <div style={{
            padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${rangoActivo === 'custom' ? 'var(--brand)' : 'var(--hairline)'}`,
            background: rangoActivo === 'custom' ? 'rgba(240,78,35,0.08)' : 'var(--soft-gray)',
            fontSize: 12, color: rangoActivo === 'custom' ? 'var(--brand)' : 'var(--muted-foreground)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ fontWeight: 600 }}>Rango</span>
            <input type="date" value={desde} onChange={(e) => setFiltro('desde', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
            <span>→</span>
            <input type="date" value={hasta} onChange={(e) => setFiltro('hasta', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <MagnifyingGlass size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)' }} />
          <input type="text" value={search} onChange={(e) => setFiltro('q', e.target.value)}
            placeholder="Buscar por nombre, neo_id o número de cuenta…"
            style={{ fontSize: 13, height: 34, padding: '0 12px 0 32px', width: '100%' }} />
        </div>
      </div>

      {error && (
        <div className="error-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning size={16} weight="fill" />
          {error}
        </div>
      )}

      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '90px 1fr 130px 130px 130px 80px 140px',
          gap: 12, padding: '10px 16px',
          borderBottom: '1px solid var(--hairline)', background: 'var(--soft-gray)',
        }}>
          <SortableHeader label="Neo.id" sortKey="neo_id" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Asociado" sortKey="nombre" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--n-600)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Cuenta</div>
          <SortableHeader label="Saldo actual" sortKey="saldo" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Aport. período" sortKey="aportado" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="# movs" sortKey="movs" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Última aport." sortKey="ultima" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
        </div>

        {loading && sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            Sin cuentas de aportaciones.
          </div>
        ) : (
          sortedRows.map((r) => {
            const ua = r.ultima_aportacion ? new Date(r.ultima_aportacion) : null
            return (
              <div key={r.cuenta_id} style={{
                display: 'grid',
                gridTemplateColumns: '90px 1fr 130px 130px 130px 80px 140px',
                gap: 12, padding: '12px 16px',
                fontSize: 13, alignItems: 'center',
                borderBottom: '1px solid var(--hairline)',
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink)' }}>
                  {r.neo_id ?? '—'}
                </div>
                <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.nombre_completo}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted-foreground)' }}>
                  {r.numero_cuenta}
                </div>
                <div style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                  {formatQ(r.saldo_actual)}
                </div>
                <div style={{ textAlign: 'right', fontFamily: 'monospace', color: r.aportado_periodo > 0 ? 'var(--ink)' : 'var(--muted-foreground)' }}>
                  {r.aportado_periodo > 0 ? formatQ(r.aportado_periodo) : '—'}
                </div>
                <div style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12, color: 'var(--muted-foreground)' }}>
                  {r.movs_periodo || '—'}
                </div>
                <div style={{ fontSize: 12 }}>
                  {ua ? (
                    <>
                      <div>{ua.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala', day: '2-digit', month: 'short' })}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted-foreground)', fontFamily: 'monospace' }}>
                        {ua.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false })}
                      </div>
                    </>
                  ) : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Reporte de Créditos
// ============================================================================

type CreditoRow = {
  id: string
  tipo_nombre: string
  asociado_id: string
  asociado_nombre: string
  asociado_neo_id: string | null
  capital_inicial: number
  saldo_capital: number
  intereses_cobrados: number
  estado: string
  fecha_desembolso: string
  dias_atraso: number
  cuotas_pendientes: number
  proxima_cuota_monto: number
  proxima_cuota_fecha: string | null
  bucket_mora: 'al_dia' | '1_30' | '31_60' | '61_90' | '90_mas' | 'castigado' | 'cancelado'
}

type CredSortKey = 'fecha' | 'nombre' | 'saldo' | 'atraso' | 'proxima' | 'estado'
type CredEstadoFilter = '' | 'al_dia' | 'en_mora' | 'castigado' | 'cancelado'

function bucketMora(dias: number, estado: string): CreditoRow['bucket_mora'] {
  if (estado === 'castigado') return 'castigado'
  if (estado === 'cancelado' || estado === 'pagado') return 'cancelado'
  if (dias === 0) return 'al_dia'
  if (dias <= 30) return '1_30'
  if (dias <= 60) return '31_60'
  if (dias <= 90) return '61_90'
  return '90_mas'
}

const BUCKET_LABEL: Record<CreditoRow['bucket_mora'], string> = {
  al_dia: 'Al día',
  '1_30': '1-30 días',
  '31_60': '31-60 días',
  '61_90': '61-90 días',
  '90_mas': '+90 días',
  castigado: 'Castigado',
  cancelado: 'Cancelado',
}

const BUCKET_COLOR: Record<CreditoRow['bucket_mora'], string> = {
  al_dia: '#0F8A57',
  '1_30': '#B8860B',
  '31_60': '#D97706',
  '61_90': '#B91C1C',
  '90_mas': '#7C1D1D',
  castigado: '#3F3F3F',
  cancelado: '#666',
}

function CreditosReporte() {
  const [params, setParams] = useSearchParams()
  const desde = params.get('desde') ?? daysAgoISO(30)
  const hasta = params.get('hasta') ?? todayISO()
  const search = params.get('q') ?? ''
  const estadoFilter = (params.get('estado') as CredEstadoFilter) ?? ''
  const sortBy = (params.get('sort') as CredSortKey) ?? 'atraso'
  const sortDir: SortDir = (params.get('dir') as SortDir) ?? 'desc'

  const rangoActivo = useMemo<'hoy' | 'ayer' | '7d' | '30d' | 'mes' | 'custom'>(() => {
    if (desde === todayISO() && hasta === todayISO()) return 'hoy'
    if (desde === daysAgoISO(1) && hasta === daysAgoISO(1)) return 'ayer'
    if (desde === daysAgoISO(7) && hasta === todayISO()) return '7d'
    if (desde === daysAgoISO(30) && hasta === todayISO()) return '30d'
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    if (desde === mesDesde && hasta === todayISO()) return 'mes'
    return 'custom'
  }, [desde, hasta])

  // Updater funcional: siempre parte del estado más reciente de la URL, así los
  // filtros (estado + fecha + orden) no se pisan entre sí al cambiarlos seguido.
  const setFiltro = (key: string, val: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      if (val) next.set(key, val)
      else next.delete(key)
      return next
    })
  }

  const setRango = (preset: 'hoy' | 'ayer' | '7d' | '30d' | 'mes') => {
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    const rangos = {
      hoy: { desde: todayISO(), hasta: todayISO() },
      ayer: { desde: daysAgoISO(1), hasta: daysAgoISO(1) },
      '7d': { desde: daysAgoISO(7), hasta: todayISO() },
      '30d': { desde: daysAgoISO(30), hasta: todayISO() },
      mes: { desde: mesDesde, hasta: todayISO() },
    }
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('desde', rangos[preset].desde)
      next.set('hasta', rangos[preset].hasta)
      return next
    })
  }

  const toggleSort = (key: CredSortKey) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      if ((prev.get('sort') ?? 'atraso') === key) {
        next.set('dir', (prev.get('dir') ?? 'desc') === 'asc' ? 'desc' : 'asc')
      } else {
        next.set('sort', key)
        next.set('dir', key === 'nombre' || key === 'estado' ? 'asc' : 'desc')
      }
      return next
    })
  }

  const [rows, setRows] = useState<CreditoRow[]>([])
  const [pagos, setPagos] = useState<{ monto: number; fecha: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Carga TODO una sola vez (no depende de fechas). Los filtros de rango se
  // aplican en memoria → cambio de fecha instantáneo, sin re-fetch.
  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: creditos, error: err1 } = await supabase
        .from('credito')
        .select(`
          id, capital_inicial, saldo_capital, intereses_cobrados, estado,
          fecha_desembolso,
          asociado:asociado_id (id, neo_id, nombre_completo),
          tipo:tipo_credito_id (nombre),
          cuotas:credito_cuota (
            numero, fecha_vencimiento, capital, interes, mora_acumulada,
            capital_pagado, interes_pagado, mora_pagada, estado, dias_atraso_actual
          )
        `)
        .order('fecha_desembolso', { ascending: false })
        .limit(1000)
      if (err1) throw err1

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped: CreditoRow[] = (creditos ?? []).map((r: any) => {
        const cuotasPend = (r.cuotas ?? [])
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((q: any) => q.estado !== 'pagada' && q.estado !== 'castigada')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .sort((a: any, b: any) => a.numero - b.numero)
        const prox = cuotasPend[0]
        const proxMonto = prox
          ? (Number(prox.capital) - Number(prox.capital_pagado)) +
            (Number(prox.interes) - Number(prox.interes_pagado)) +
            (Number(prox.mora_acumulada) - Number(prox.mora_pagada))
          : 0
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const maxAtraso = cuotasPend.reduce((m: number, q: any) => Math.max(m, Number(q.dias_atraso_actual) || 0), 0)
        return {
          id: r.id,
          tipo_nombre: r.tipo?.nombre ?? 'Crédito',
          asociado_id: r.asociado?.id ?? '',
          asociado_nombre: r.asociado?.nombre_completo ?? '—',
          asociado_neo_id: r.asociado?.neo_id ?? null,
          capital_inicial: Number(r.capital_inicial ?? 0),
          saldo_capital: Number(r.saldo_capital ?? 0),
          intereses_cobrados: Number(r.intereses_cobrados ?? 0),
          estado: r.estado,
          fecha_desembolso: r.fecha_desembolso,
          dias_atraso: maxAtraso,
          cuotas_pendientes: cuotasPend.length,
          proxima_cuota_monto: proxMonto,
          proxima_cuota_fecha: prox?.fecha_vencimiento ?? null,
          bucket_mora: bucketMora(maxAtraso, r.estado ?? ''),
        }
      })

      // Todos los pagos (se filtran por fecha en memoria abajo)
      const { data: pagosData, error: err2 } = await supabase
        .from('credito_pago')
        .select('aplicado_capital, aplicado_interes, aplicado_mora, fecha_pago')
        .limit(20000)
      if (err2) throw err2
      const pagosMapped = (pagosData ?? []).map((p) => ({
        fecha: (p.fecha_pago ?? '').slice(0, 10),
        monto: Number(p.aplicado_capital ?? 0) + Number(p.aplicado_interes ?? 0) + Number(p.aplicado_mora ?? 0),
      }))

      setPagos(pagosMapped)
      setRows(mapped)
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any
      const msg = err?.message ?? err?.details ?? err?.hint ?? JSON.stringify(err) ?? 'Error desconocido'
      setError(msg)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (estadoFilter === 'al_dia' && r.bucket_mora !== 'al_dia') return false
      if (estadoFilter === 'en_mora' && !['1_30', '31_60', '61_90', '90_mas'].includes(r.bucket_mora)) return false
      if (estadoFilter === 'castigado' && r.bucket_mora !== 'castigado') return false
      if (estadoFilter === 'cancelado' && r.bucket_mora !== 'cancelado') return false
      if (q && !(r.asociado_nombre.toLowerCase().includes(q) || r.asociado_neo_id?.includes(q) || r.tipo_nombre.toLowerCase().includes(q))) return false
      return true
    })
  }, [rows, search, estadoFilter])

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    const cmp = (a: CreditoRow, b: CreditoRow) => {
      switch (sortBy) {
        case 'fecha': return a.fecha_desembolso.localeCompare(b.fecha_desembolso) * dir
        case 'nombre': return a.asociado_nombre.localeCompare(b.asociado_nombre, 'es') * dir
        case 'saldo': return (a.saldo_capital - b.saldo_capital) * dir
        case 'atraso': return (a.dias_atraso - b.dias_atraso) * dir
        case 'proxima':
          return (
            (new Date(a.proxima_cuota_fecha ?? 0).getTime() - new Date(b.proxima_cuota_fecha ?? 0).getTime()) * dir
          )
        case 'estado': return a.bucket_mora.localeCompare(b.bucket_mora) * dir
        default: return 0
      }
    }
    return [...filtered].sort(cmp)
  }, [filtered, sortBy, sortDir])

  // Desembolsado/cobrado del período — calculados en memoria según desde/hasta
  const desembolsadoPeriodo = useMemo(
    () => rows.filter((c) => c.fecha_desembolso >= desde && c.fecha_desembolso <= hasta)
             .reduce((s, c) => s + c.capital_inicial, 0),
    [rows, desde, hasta],
  )
  const cobradoPeriodo = useMemo(
    () => pagos.filter((p) => p.fecha >= desde && p.fecha <= hasta).reduce((s, p) => s + p.monto, 0),
    [pagos, desde, hasta],
  )

  const kpis = useMemo(() => {
    const vigentes = rows.filter((r) => r.saldo_capital > 0 && r.bucket_mora !== 'castigado' && r.bucket_mora !== 'cancelado')
    const carteraTotal = vigentes.reduce((s, r) => s + r.saldo_capital, 0)
    const enMora = vigentes.filter((r) => r.dias_atraso > 0)
    const carteraMora = enMora.reduce((s, r) => s + r.saldo_capital, 0)
    return {
      carteraTotal,
      carteraMora,
      creditosVigentes: vigentes.length,
      enMoraCount: enMora.length,
      desembolsadoPeriodo,
      cobradoPeriodo,
    }
  }, [rows, desembolsadoPeriodo, cobradoPeriodo])

  const buckets = useMemo(() => {
    const b: Record<CreditoRow['bucket_mora'], { count: number; monto: number }> = {
      al_dia: { count: 0, monto: 0 },
      '1_30': { count: 0, monto: 0 },
      '31_60': { count: 0, monto: 0 },
      '61_90': { count: 0, monto: 0 },
      '90_mas': { count: 0, monto: 0 },
      castigado: { count: 0, monto: 0 },
      cancelado: { count: 0, monto: 0 },
    }
    for (const r of rows) {
      if (r.saldo_capital <= 0 && r.bucket_mora !== 'castigado') continue
      b[r.bucket_mora].count += 1
      b[r.bucket_mora].monto += r.saldo_capital
    }
    return b
  }, [rows])

  const exportCSV = () => {
    const header = [
      'neo_id', 'asociado', 'tipo', 'fecha_desembolso',
      'capital_inicial', 'saldo_capital', 'intereses_cobrados',
      'cuotas_pendientes', 'dias_atraso', 'estado', 'bucket_mora',
      'proxima_cuota_monto', 'proxima_cuota_fecha',
    ]
    const escape = (v: unknown): string => {
      if (v === null || v === undefined) return ''
      const s = String(v).replace(/"/g, '""')
      return /[",\n]/.test(s) ? `"${s}"` : s
    }
    const lines = [header.join(',')]
    for (const r of sortedRows) {
      lines.push([
        r.asociado_neo_id ?? '', r.asociado_nombre, r.tipo_nombre, r.fecha_desembolso,
        r.capital_inicial.toFixed(2), r.saldo_capital.toFixed(2), r.intereses_cobrados.toFixed(2),
        r.cuotas_pendientes, r.dias_atraso, r.estado, BUCKET_LABEL[r.bucket_mora],
        r.proxima_cuota_monto.toFixed(2), r.proxima_cuota_fecha ?? '',
      ].map(escape).join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `creditos_${desde}_a_${hasta}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Reporte
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>
            Créditos
          </h2>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
            {loading ? 'Cargando…' : `${filtered.length} de ${rows.length} créditos · cartera Q${kpis.carteraTotal.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <ArrowsClockwise size={14} />
            Refrescar
          </button>
          <button onClick={exportCSV} disabled={sortedRows.length === 0} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <Download size={14} />
            Exportar CSV
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <KpiTile label="Cartera vigente" value={formatQ(kpis.carteraTotal)} tone="success" />
        <KpiTile label={`En mora (${kpis.enMoraCount})`} value={formatQ(kpis.carteraMora)} tone={kpis.carteraMora > 0 ? 'danger' : undefined} />
        <KpiTile label="Desembolsado (período)" value={formatQ(kpis.desembolsadoPeriodo)} />
        <KpiTile label="Cobrado (período)" value={formatQ(kpis.cobradoPeriodo)} />
      </div>

      {/* Buckets de mora */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, padding: '12px 16px',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
          Distribución por mora
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
          {(['al_dia', '1_30', '31_60', '61_90', '90_mas'] as const).map((b) => {
            const bd = buckets[b]
            return (
              <div key={b} style={{
                padding: '10px 12px', borderRadius: 8,
                background: 'var(--soft-gray)',
                borderLeft: `3px solid ${BUCKET_COLOR[b]}`,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: BUCKET_COLOR[b], textTransform: 'uppercase' }}>
                  {BUCKET_LABEL[b]}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>
                  {formatQ(bd.monto)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                  {bd.count} {bd.count === 1 ? 'crédito' : 'créditos'}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div style={{
        padding: '14px 16px', background: 'var(--surface)',
        border: '1px solid var(--hairline)', borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['hoy', 'ayer', '7d', '30d', 'mes'] as const).map((preset) => {
            const labels = { hoy: 'Hoy', ayer: 'Ayer', '7d': 'Últimos 7 días', '30d': 'Últimos 30 días', mes: 'Este mes' }
            const active = rangoActivo === preset
            return (
              <button
                key={preset}
                onClick={() => setRango(preset)}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  border: `1px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
                  background: active ? 'var(--brand)' : 'transparent',
                  color: active ? 'white' : 'var(--ink)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                {labels[preset]}
              </button>
            )
          })}
          <div style={{
            padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${rangoActivo === 'custom' ? 'var(--brand)' : 'var(--hairline)'}`,
            background: rangoActivo === 'custom' ? 'rgba(240,78,35,0.08)' : 'var(--soft-gray)',
            fontSize: 12, color: rangoActivo === 'custom' ? 'var(--brand)' : 'var(--muted-foreground)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ fontWeight: 600 }}>Rango</span>
            <input type="date" value={desde} onChange={(e) => setFiltro('desde', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
            <span>→</span>
            <input type="date" value={hasta} onChange={(e) => setFiltro('hasta', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
            <MagnifyingGlass size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)' }} />
            <input type="text" value={search} onChange={(e) => setFiltro('q', e.target.value)}
              placeholder="Buscar por asociado, neo_id o tipo…"
              style={{ fontSize: 13, height: 34, padding: '0 12px 0 32px', width: '100%' }} />
          </div>
          <div style={{ display: 'inline-flex', background: 'var(--soft-gray)', borderRadius: 8, padding: 2, gap: 2 }}>
            {[
              { v: '' as CredEstadoFilter, label: 'Todos' },
              { v: 'al_dia' as CredEstadoFilter, label: 'Al día' },
              { v: 'en_mora' as CredEstadoFilter, label: 'En mora' },
              { v: 'castigado' as CredEstadoFilter, label: 'Castigado' },
              { v: 'cancelado' as CredEstadoFilter, label: 'Cancelado' },
            ].map((opt) => {
              const active = estadoFilter === opt.v
              return (
                <button key={opt.v} onClick={() => setFiltro('estado', opt.v)}
                  style={{
                    padding: '6px 12px', borderRadius: 6, border: 'none',
                    background: active ? 'var(--surface)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--muted-foreground)',
                    fontSize: 12, fontWeight: active ? 600 : 500, cursor: 'pointer',
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}>
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning size={16} weight="fill" />
          {error}
        </div>
      )}

      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '110px 1fr 130px 100px 100px 130px 110px',
          gap: 12, padding: '10px 16px',
          borderBottom: '1px solid var(--hairline)', background: 'var(--soft-gray)',
        }}>
          <SortableHeader label="Desembolso" sortKey="fecha" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Asociado / tipo" sortKey="nombre" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Saldo capital" sortKey="saldo" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Días mora" sortKey="atraso" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Estado" sortKey="estado" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Próx. cuota" sortKey="proxima" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <div></div>
        </div>

        {loading && sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            Sin créditos con estos filtros.
          </div>
        ) : (
          sortedRows.map((r) => {
            const proxDate = r.proxima_cuota_fecha ? new Date(r.proxima_cuota_fecha + 'T00:00:00') : null
            return (
              <div key={r.id} style={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr 130px 100px 100px 130px 110px',
                gap: 12, padding: '12px 16px',
                fontSize: 13, alignItems: 'center',
                borderBottom: '1px solid var(--hairline)',
              }}>
                <div style={{ fontSize: 12 }}>
                  {new Date(r.fecha_desembolso + 'T12:00:00').toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Guatemala' })}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.asociado_nombre}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                    {r.asociado_neo_id ? `${r.asociado_neo_id} · ` : ''}{r.tipo_nombre}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                  {formatQ(r.saldo_capital)}
                </div>
                <div style={{ textAlign: 'right', fontFamily: 'monospace', color: r.dias_atraso > 0 ? BUCKET_COLOR[r.bucket_mora] : 'var(--muted-foreground)', fontWeight: r.dias_atraso > 0 ? 600 : 400 }}>
                  {r.dias_atraso > 0 ? r.dias_atraso : '—'}
                </div>
                <div>
                  <span style={{
                    padding: '3px 8px', borderRadius: 999,
                    background: `${BUCKET_COLOR[r.bucket_mora]}18`,
                    color: BUCKET_COLOR[r.bucket_mora],
                    fontSize: 11, fontWeight: 600, display: 'inline-block',
                  }}>
                    {BUCKET_LABEL[r.bucket_mora]}
                  </span>
                </div>
                <div style={{ fontSize: 12 }}>
                  {proxDate ? (
                    <>
                      <div>{proxDate.toLocaleDateString('es-GT', { day: '2-digit', month: 'short' })}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted-foreground)', fontFamily: 'monospace' }}>
                        {formatQ(r.proxima_cuota_monto)}
                      </div>
                    </>
                  ) : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textAlign: 'right' }}>
                  {r.cuotas_pendientes} pend.
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Reporte de Caja Histórica (sesiones)
// ============================================================================

type SesionRow = {
  id: string
  caja_id: string
  caja_nombre: string
  estado: 'abierta' | 'cerrada'
  monto_apertura: number
  monto_cierre_teorico: number | null
  monto_cierre_real: number | null
  diferencial: number | null
  motivo_diferencial: string | null
  abierta_at: string
  cerrada_at: string | null
  ingresos: number
  egresos: number
  movs_count: number
  movs_anulados: number
}

type CajaSortKey = 'apertura' | 'cierre' | 'ingresos' | 'egresos' | 'diferencial' | 'caja' | 'estado'
type CajaEstadoFilter = '' | 'abierta' | 'cerrada' | 'con_diferencial'

function CajaHistoricaReporte() {
  const [params, setParams] = useSearchParams()
  const desde = params.get('desde') ?? daysAgoISO(30)
  const hasta = params.get('hasta') ?? todayISO()
  const search = params.get('q') ?? ''
  const estadoFilter = (params.get('estado') as CajaEstadoFilter) ?? ''
  const sortBy = (params.get('sort') as CajaSortKey) ?? 'apertura'
  const sortDir: SortDir = (params.get('dir') as SortDir) ?? 'desc'

  const rangoActivo = useMemo<'hoy' | 'ayer' | '7d' | '30d' | 'mes' | 'custom'>(() => {
    if (desde === todayISO() && hasta === todayISO()) return 'hoy'
    if (desde === daysAgoISO(1) && hasta === daysAgoISO(1)) return 'ayer'
    if (desde === daysAgoISO(7) && hasta === todayISO()) return '7d'
    if (desde === daysAgoISO(30) && hasta === todayISO()) return '30d'
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    if (desde === mesDesde && hasta === todayISO()) return 'mes'
    return 'custom'
  }, [desde, hasta])

  const setFiltro = (key: string, val: string) => {
    const next = new URLSearchParams(window.location.search)
    if (val) next.set(key, val)
    else next.delete(key)
    setParams(next)
  }

  const setRango = (preset: 'hoy' | 'ayer' | '7d' | '30d' | 'mes') => {
    const next = new URLSearchParams(window.location.search)
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    const rangos = {
      hoy: { desde: todayISO(), hasta: todayISO() },
      ayer: { desde: daysAgoISO(1), hasta: daysAgoISO(1) },
      '7d': { desde: daysAgoISO(7), hasta: todayISO() },
      '30d': { desde: daysAgoISO(30), hasta: todayISO() },
      mes: { desde: mesDesde, hasta: todayISO() },
    }
    next.set('desde', rangos[preset].desde)
    next.set('hasta', rangos[preset].hasta)
    setParams(next)
  }

  const toggleSort = (key: CajaSortKey) => {
    const next = new URLSearchParams(window.location.search)
    if (sortBy === key) {
      next.set('dir', sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      next.set('sort', key)
      next.set('dir', key === 'caja' || key === 'estado' ? 'asc' : 'desc')
    }
    setParams(next)
  }

  const [rows, setRows] = useState<SesionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: sesiones, error: err1 } = await supabase
        .from('caja_sesion')
        .select(`
          id, caja_id, estado, monto_apertura,
          monto_cierre_teorico, monto_cierre_real, diferencial, motivo_diferencial,
          abierta_at, cerrada_at,
          caja:caja_id (nombre, codigo)
        `)
        .gte('abierta_at', `${desde}T00:00:00-06:00`)
        .lte('abierta_at', `${hasta}T23:59:59.999-06:00`)
        .order('abierta_at', { ascending: false })
        .limit(500)
      if (err1) throw err1

      const sesionIds = (sesiones ?? []).map((s) => (s as { id: string }).id)
      if (sesionIds.length === 0) {
        setRows([])
        return
      }

      const { data: movs, error: err2 } = await supabase
        .from('movimiento_caja')
        .select(`
          id, caja_sesion_id, monto, anulado,
          tipo_movimiento:tipo_movimiento_caja!inner (clase)
        `)
        .in('caja_sesion_id', sesionIds)
        .limit(20000)
      if (err2) throw err2

      const agg: Record<string, { ingresos: number; egresos: number; count: number; anulados: number }> = {}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const m of (movs ?? []) as any[]) {
        const sid = m.caja_sesion_id as string
        if (!agg[sid]) agg[sid] = { ingresos: 0, egresos: 0, count: 0, anulados: 0 }
        agg[sid].count += 1
        if (m.anulado) {
          agg[sid].anulados += 1
          continue
        }
        const clase = m.tipo_movimiento?.clase as string
        const monto = Number(m.monto ?? 0)
        if (clase === 'INGRESO') agg[sid].ingresos += monto
        else if (clase === 'EGRESO') agg[sid].egresos += monto
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped: SesionRow[] = ((sesiones ?? []) as any[]).map((s) => ({
        id: s.id,
        caja_id: s.caja_id,
        caja_nombre: s.caja?.nombre ?? s.caja?.codigo ?? '—',
        estado: s.estado,
        monto_apertura: Number(s.monto_apertura ?? 0),
        monto_cierre_teorico: s.monto_cierre_teorico != null ? Number(s.monto_cierre_teorico) : null,
        monto_cierre_real: s.monto_cierre_real != null ? Number(s.monto_cierre_real) : null,
        diferencial: s.diferencial != null ? Number(s.diferencial) : null,
        motivo_diferencial: s.motivo_diferencial ?? null,
        abierta_at: s.abierta_at,
        cerrada_at: s.cerrada_at,
        ingresos: agg[s.id]?.ingresos ?? 0,
        egresos: agg[s.id]?.egresos ?? 0,
        movs_count: agg[s.id]?.count ?? 0,
        movs_anulados: agg[s.id]?.anulados ?? 0,
      }))

      setRows(mapped)
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any
      const msg = err?.message ?? err?.details ?? err?.hint ?? JSON.stringify(err) ?? 'Error desconocido'
      setError(msg)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [desde, hasta])

  useEffect(() => { void load() }, [load])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (estadoFilter === 'abierta' && r.estado !== 'abierta') return false
      if (estadoFilter === 'cerrada' && r.estado !== 'cerrada') return false
      if (estadoFilter === 'con_diferencial' && !(r.diferencial && Math.abs(r.diferencial) > 0.01)) return false
      if (q && !r.caja_nombre.toLowerCase().includes(q)) return false
      return true
    })
  }, [rows, search, estadoFilter])

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    const cmp = (a: SesionRow, b: SesionRow) => {
      switch (sortBy) {
        case 'apertura': return (new Date(a.abierta_at).getTime() - new Date(b.abierta_at).getTime()) * dir
        case 'cierre':
          return (new Date(a.cerrada_at ?? 0).getTime() - new Date(b.cerrada_at ?? 0).getTime()) * dir
        case 'ingresos': return (a.ingresos - b.ingresos) * dir
        case 'egresos': return (a.egresos - b.egresos) * dir
        case 'diferencial':
          return (Math.abs(a.diferencial ?? 0) - Math.abs(b.diferencial ?? 0)) * dir
        case 'caja': return a.caja_nombre.localeCompare(b.caja_nombre, 'es') * dir
        case 'estado': return a.estado.localeCompare(b.estado) * dir
        default: return 0
      }
    }
    return [...filtered].sort(cmp)
  }, [filtered, sortBy, sortDir])

  const kpis = useMemo(() => {
    const abiertas = rows.filter((r) => r.estado === 'abierta').length
    const cerradas = rows.filter((r) => r.estado === 'cerrada').length
    const ingresos = rows.reduce((s, r) => s + r.ingresos, 0)
    const egresos = rows.reduce((s, r) => s + r.egresos, 0)
    const conDiferencial = rows.filter((r) => r.diferencial != null && Math.abs(r.diferencial) > 0.01)
    const diferencialTotal = conDiferencial.reduce((s, r) => s + (r.diferencial ?? 0), 0)
    return { abiertas, cerradas, ingresos, egresos, conDiferencial: conDiferencial.length, diferencialTotal }
  }, [rows])

  const exportCSV = () => {
    const header = [
      'caja', 'estado',
      'apertura_fecha', 'apertura_hora', 'monto_apertura',
      'cierre_fecha', 'cierre_hora', 'monto_cierre_teorico', 'monto_cierre_real',
      'diferencial', 'motivo_diferencial',
      'ingresos', 'egresos', 'total_movs', 'movs_anulados',
    ]
    const escape = (v: unknown): string => {
      if (v === null || v === undefined) return ''
      const s = String(v).replace(/"/g, '""')
      return /[",\n]/.test(s) ? `"${s}"` : s
    }
    const lines = [header.join(',')]
    for (const r of sortedRows) {
      const ap = new Date(r.abierta_at)
      const ci = r.cerrada_at ? new Date(r.cerrada_at) : null
      lines.push([
        r.caja_nombre, r.estado,
        ap.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala' }),
        ap.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false }),
        r.monto_apertura.toFixed(2),
        ci ? ci.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala' }) : '',
        ci ? ci.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false }) : '',
        r.monto_cierre_teorico?.toFixed(2) ?? '',
        r.monto_cierre_real?.toFixed(2) ?? '',
        r.diferencial?.toFixed(2) ?? '',
        r.motivo_diferencial ?? '',
        r.ingresos.toFixed(2), r.egresos.toFixed(2),
        r.movs_count, r.movs_anulados,
      ].map(escape).join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `caja_${desde}_a_${hasta}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Reporte
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>
            Caja histórica
          </h2>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
            {loading ? 'Cargando…' : `${filtered.length} de ${rows.length} sesiones · ${kpis.cerradas} cerradas · ${kpis.abiertas} abiertas`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <ArrowsClockwise size={14} />
            Refrescar
          </button>
          <button onClick={exportCSV} disabled={sortedRows.length === 0} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <Download size={14} />
            Exportar CSV
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <KpiTile label="Ingresos" value={formatQ(kpis.ingresos)} tone="success" />
        <KpiTile label="Egresos" value={formatQ(kpis.egresos)} tone="danger" />
        <KpiTile label="Neto" value={formatQ(kpis.ingresos - kpis.egresos)} tone={kpis.ingresos - kpis.egresos >= 0 ? 'success' : 'danger'} />
        <KpiTile label={`Diferencial (${kpis.conDiferencial})`} value={formatQ(kpis.diferencialTotal)} tone={Math.abs(kpis.diferencialTotal) > 0.01 ? 'danger' : undefined} />
      </div>

      <div style={{
        padding: '14px 16px', background: 'var(--surface)',
        border: '1px solid var(--hairline)', borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['hoy', 'ayer', '7d', '30d', 'mes'] as const).map((preset) => {
            const labels = { hoy: 'Hoy', ayer: 'Ayer', '7d': 'Últimos 7 días', '30d': 'Últimos 30 días', mes: 'Este mes' }
            const active = rangoActivo === preset
            return (
              <button
                key={preset}
                onClick={() => setRango(preset)}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  border: `1px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
                  background: active ? 'var(--brand)' : 'transparent',
                  color: active ? 'white' : 'var(--ink)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                {labels[preset]}
              </button>
            )
          })}
          <div style={{
            padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${rangoActivo === 'custom' ? 'var(--brand)' : 'var(--hairline)'}`,
            background: rangoActivo === 'custom' ? 'rgba(240,78,35,0.08)' : 'var(--soft-gray)',
            fontSize: 12, color: rangoActivo === 'custom' ? 'var(--brand)' : 'var(--muted-foreground)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ fontWeight: 600 }}>Rango</span>
            <input type="date" value={desde} onChange={(e) => setFiltro('desde', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
            <span>→</span>
            <input type="date" value={hasta} onChange={(e) => setFiltro('hasta', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
            <MagnifyingGlass size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)' }} />
            <input type="text" value={search} onChange={(e) => setFiltro('q', e.target.value)}
              placeholder="Buscar por caja…"
              style={{ fontSize: 13, height: 34, padding: '0 12px 0 32px', width: '100%' }} />
          </div>
          <div style={{ display: 'inline-flex', background: 'var(--soft-gray)', borderRadius: 8, padding: 2, gap: 2 }}>
            {[
              { v: '' as CajaEstadoFilter, label: 'Todas' },
              { v: 'abierta' as CajaEstadoFilter, label: 'Abiertas' },
              { v: 'cerrada' as CajaEstadoFilter, label: 'Cerradas' },
              { v: 'con_diferencial' as CajaEstadoFilter, label: 'Con diferencial' },
            ].map((opt) => {
              const active = estadoFilter === opt.v
              return (
                <button key={opt.v} onClick={() => setFiltro('estado', opt.v)}
                  style={{
                    padding: '6px 12px', borderRadius: 6, border: 'none',
                    background: active ? 'var(--surface)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--muted-foreground)',
                    fontSize: 12, fontWeight: active ? 600 : 500, cursor: 'pointer',
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}>
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning size={16} weight="fill" />
          {error}
        </div>
      )}

      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '140px 130px 130px 110px 110px 110px 90px 40px',
          gap: 12, padding: '10px 16px',
          borderBottom: '1px solid var(--hairline)', background: 'var(--soft-gray)',
        }}>
          <SortableHeader label="Caja" sortKey="caja" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Apertura" sortKey="apertura" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Cierre" sortKey="cierre" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Ingresos" sortKey="ingresos" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Egresos" sortKey="egresos" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Diferencial" sortKey="diferencial" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Estado" sortKey="estado" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <div></div>
        </div>

        {loading && sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            Sin sesiones con estos filtros.
          </div>
        ) : (
          sortedRows.map((r) => {
            const ap = new Date(r.abierta_at)
            const ci = r.cerrada_at ? new Date(r.cerrada_at) : null
            const isExpanded = expanded === r.id
            const dif = r.diferencial ?? 0
            const difSignificativo = Math.abs(dif) > 0.01
            return (
              <div key={r.id}>
                <div
                  onClick={() => setExpanded(isExpanded ? null : r.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '140px 130px 130px 110px 110px 110px 90px 40px',
                    gap: 12, padding: '12px 16px',
                    fontSize: 13, alignItems: 'center', cursor: 'pointer',
                    borderBottom: isExpanded ? 'none' : '1px solid var(--hairline)',
                    background: isExpanded ? 'var(--soft-gray)' : 'transparent',
                  }}
                >
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.caja_nombre}
                  </div>
                  <div style={{ fontSize: 12 }}>
                    <div>{ap.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala', day: '2-digit', month: 'short' })}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted-foreground)', fontFamily: 'monospace' }}>
                      {ap.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false })}
                    </div>
                  </div>
                  <div style={{ fontSize: 12 }}>
                    {ci ? (
                      <>
                        <div>{ci.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala', day: '2-digit', month: 'short' })}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted-foreground)', fontFamily: 'monospace' }}>
                          {ci.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false })}
                        </div>
                      </>
                    ) : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                  </div>
                  <div style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--positive)' }}>
                    {formatQ(r.ingresos)}
                  </div>
                  <div style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--brand)' }}>
                    {formatQ(r.egresos)}
                  </div>
                  <div style={{
                    textAlign: 'right', fontFamily: 'monospace',
                    fontWeight: difSignificativo ? 700 : 400,
                    color: difSignificativo ? (dif < 0 ? 'var(--brand)' : '#B8860B') : 'var(--muted-foreground)',
                  }}>
                    {r.diferencial != null ? formatQ(r.diferencial) : '—'}
                  </div>
                  <div>
                    <span style={{
                      padding: '3px 8px', borderRadius: 999,
                      background: r.estado === 'abierta' ? 'rgba(240,78,35,0.10)' : 'rgba(15,138,87,0.10)',
                      color: r.estado === 'abierta' ? 'var(--brand)' : 'var(--positive)',
                      fontSize: 11, fontWeight: 600, display: 'inline-block',
                    }}>
                      {r.estado === 'abierta' ? 'Abierta' : 'Cerrada'}
                    </span>
                  </div>
                  <div style={{ textAlign: 'center', color: 'var(--muted-foreground)' }}>
                    {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{
                    padding: '12px 16px 16px', background: 'var(--soft-gray)',
                    borderBottom: '1px solid var(--hairline)',
                    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14,
                    fontSize: 12,
                  }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                        Apertura
                      </div>
                      <div style={{ marginTop: 2, fontFamily: 'monospace' }}>{formatQ(r.monto_apertura)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                        Cierre teórico
                      </div>
                      <div style={{ marginTop: 2, fontFamily: 'monospace' }}>
                        {r.monto_cierre_teorico != null ? formatQ(r.monto_cierre_teorico) : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                        Cierre real
                      </div>
                      <div style={{ marginTop: 2, fontFamily: 'monospace' }}>
                        {r.monto_cierre_real != null ? formatQ(r.monto_cierre_real) : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                        Movimientos
                      </div>
                      <div style={{ marginTop: 2 }}>
                        {r.movs_count} {r.movs_count === 1 ? 'operación' : 'operaciones'}
                        {r.movs_anulados > 0 && (
                          <span style={{ color: 'var(--brand)' }}> · {r.movs_anulados} anulad{r.movs_anulados === 1 ? 'a' : 'as'}</span>
                        )}
                      </div>
                    </div>
                    {r.motivo_diferencial && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                          Motivo del diferencial
                        </div>
                        <div style={{ marginTop: 2 }}>{r.motivo_diferencial}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Reporte de Bancos (boletas por cuenta bancaria propia)
// ============================================================================

type BoletaRow = {
  id: string
  fecha_boleta: string
  numero_boleta: string
  banco: string
  cuenta_banco: string
  cuenta_alias: string | null
  monto_total: number
  monto_aplicado: number
  disponible: number
  estado: 'abierta' | 'conciliada' | 'anulada'
  notas: string | null
  created_at: string
}

type BancoSortKey = 'fecha' | 'banco' | 'numero' | 'total' | 'aplicado' | 'disponible' | 'estado'
type BancoEstadoFilter = '' | 'abierta' | 'conciliada' | 'anulada' | 'con_disponible'

const BOLETA_LABEL: Record<'abierta' | 'conciliada' | 'anulada', string> = {
  abierta: 'Abierta',
  conciliada: 'Conciliada',
  anulada: 'Anulada',
}

const BOLETA_COLOR: Record<'abierta' | 'conciliada' | 'anulada', string> = {
  abierta: '#B8860B',
  conciliada: '#0F8A57',
  anulada: '#666',
}

function BancosReporte() {
  const [params, setParams] = useSearchParams()
  const desde = params.get('desde') ?? daysAgoISO(30)
  const hasta = params.get('hasta') ?? todayISO()
  const search = params.get('q') ?? ''
  const estadoFilter = (params.get('estado') as BancoEstadoFilter) ?? ''
  const sortBy = (params.get('sort') as BancoSortKey) ?? 'fecha'
  const sortDir: SortDir = (params.get('dir') as SortDir) ?? 'desc'

  const rangoActivo = useMemo<'hoy' | 'ayer' | '7d' | '30d' | 'mes' | 'custom'>(() => {
    if (desde === todayISO() && hasta === todayISO()) return 'hoy'
    if (desde === daysAgoISO(1) && hasta === daysAgoISO(1)) return 'ayer'
    if (desde === daysAgoISO(7) && hasta === todayISO()) return '7d'
    if (desde === daysAgoISO(30) && hasta === todayISO()) return '30d'
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    if (desde === mesDesde && hasta === todayISO()) return 'mes'
    return 'custom'
  }, [desde, hasta])

  const setFiltro = (key: string, val: string) => {
    const next = new URLSearchParams(window.location.search)
    if (val) next.set(key, val)
    else next.delete(key)
    setParams(next)
  }

  const setRango = (preset: 'hoy' | 'ayer' | '7d' | '30d' | 'mes') => {
    const next = new URLSearchParams(window.location.search)
    const mesDesde = `${hoyGT().slice(0, 7)}-01`
    const rangos = {
      hoy: { desde: todayISO(), hasta: todayISO() },
      ayer: { desde: daysAgoISO(1), hasta: daysAgoISO(1) },
      '7d': { desde: daysAgoISO(7), hasta: todayISO() },
      '30d': { desde: daysAgoISO(30), hasta: todayISO() },
      mes: { desde: mesDesde, hasta: todayISO() },
    }
    next.set('desde', rangos[preset].desde)
    next.set('hasta', rangos[preset].hasta)
    setParams(next)
  }

  const toggleSort = (key: BancoSortKey) => {
    const next = new URLSearchParams(window.location.search)
    if (sortBy === key) {
      next.set('dir', sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      next.set('sort', key)
      next.set('dir', key === 'banco' || key === 'numero' || key === 'estado' ? 'asc' : 'desc')
    }
    setParams(next)
  }

  const [rows, setRows] = useState<BoletaRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await supabase
        .from('boleta_bancaria')
        .select(`
          id, fecha_boleta, numero_boleta, monto_total, monto_aplicado, estado, notas, created_at,
          cuenta:cuenta_bancaria_propia_id (banco, numero_cuenta, alias)
        `)
        .gte('fecha_boleta', desde)
        .lte('fecha_boleta', hasta)
        .order('fecha_boleta', { ascending: false })
        .limit(2000)
      if (err) throw err

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped: BoletaRow[] = (data ?? []).map((r: any) => {
        const total = Number(r.monto_total ?? 0)
        const aplicado = Number(r.monto_aplicado ?? 0)
        return {
          id: r.id,
          fecha_boleta: r.fecha_boleta,
          numero_boleta: r.numero_boleta ?? '',
          banco: r.cuenta?.banco ?? '—',
          cuenta_banco: r.cuenta?.numero_cuenta ?? '',
          cuenta_alias: r.cuenta?.alias ?? null,
          monto_total: total,
          monto_aplicado: aplicado,
          disponible: total - aplicado,
          estado: r.estado ?? 'abierta',
          notas: r.notas ?? null,
          created_at: r.created_at,
        }
      })
      setRows(mapped)
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any
      const msg = err?.message ?? err?.details ?? err?.hint ?? JSON.stringify(err) ?? 'Error desconocido'
      setError(msg)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [desde, hasta])

  useEffect(() => { void load() }, [load])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (estadoFilter && estadoFilter !== 'con_disponible' && r.estado !== estadoFilter) return false
      if (estadoFilter === 'con_disponible' && !(r.disponible > 0.01 && r.estado !== 'anulada')) return false
      if (q) {
        const matches =
          r.banco.toLowerCase().includes(q) ||
          r.numero_boleta.toLowerCase().includes(q) ||
          r.cuenta_banco.includes(q) ||
          r.cuenta_alias?.toLowerCase().includes(q)
        if (!matches) return false
      }
      return true
    })
  }, [rows, search, estadoFilter])

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    const cmp = (a: BoletaRow, b: BoletaRow) => {
      switch (sortBy) {
        case 'fecha':      return a.fecha_boleta.localeCompare(b.fecha_boleta) * dir
        case 'banco':      return a.banco.localeCompare(b.banco, 'es') * dir
        case 'numero':     return a.numero_boleta.localeCompare(b.numero_boleta, 'es', { numeric: true }) * dir
        case 'total':      return (a.monto_total - b.monto_total) * dir
        case 'aplicado':   return (a.monto_aplicado - b.monto_aplicado) * dir
        case 'disponible': return (a.disponible - b.disponible) * dir
        case 'estado':     return a.estado.localeCompare(b.estado) * dir
        default: return 0
      }
    }
    return [...filtered].sort(cmp)
  }, [filtered, sortBy, sortDir])

  const kpis = useMemo(() => {
    // Solo boletas NO anuladas cuentan como capital movido
    const vigentes = rows.filter((r) => r.estado !== 'anulada')
    const total = vigentes.reduce((s, r) => s + r.monto_total, 0)
    const aplicado = vigentes.reduce((s, r) => s + r.monto_aplicado, 0)
    const disponible = vigentes.reduce((s, r) => s + Math.max(0, r.disponible), 0)
    const conciliadas = rows.filter((r) => r.estado === 'conciliada').length
    const abiertas = rows.filter((r) => r.estado === 'abierta').length
    const anuladas = rows.filter((r) => r.estado === 'anulada').length
    return { total, aplicado, disponible, conciliadas, abiertas, anuladas }
  }, [rows])

  const porBanco = useMemo(() => {
    const agg: Record<string, { count: number; total: number; disponible: number }> = {}
    for (const r of rows) {
      if (r.estado === 'anulada') continue
      if (!agg[r.banco]) agg[r.banco] = { count: 0, total: 0, disponible: 0 }
      agg[r.banco].count += 1
      agg[r.banco].total += r.monto_total
      agg[r.banco].disponible += Math.max(0, r.disponible)
    }
    return Object.entries(agg).sort((a, b) => b[1].total - a[1].total)
  }, [rows])

  const exportCSV = () => {
    const header = [
      'fecha_boleta', 'banco', 'cuenta', 'alias', 'numero_boleta',
      'monto_total', 'monto_aplicado', 'disponible', 'estado', 'notas',
      'created_fecha', 'created_hora',
    ]
    const escape = (v: unknown): string => {
      if (v === null || v === undefined) return ''
      const s = String(v).replace(/"/g, '""')
      return /[",\n]/.test(s) ? `"${s}"` : s
    }
    const lines = [header.join(',')]
    for (const r of sortedRows) {
      const d = new Date(r.created_at)
      lines.push([
        r.fecha_boleta, r.banco, r.cuenta_banco, r.cuenta_alias ?? '', r.numero_boleta,
        r.monto_total.toFixed(2), r.monto_aplicado.toFixed(2), r.disponible.toFixed(2),
        BOLETA_LABEL[r.estado], r.notas ?? '',
        d.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala' }),
        d.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala', hour12: false }),
      ].map(escape).join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bancos_${desde}_a_${hasta}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Reporte
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>
            Bancos
          </h2>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
            {loading ? 'Cargando…' : `${filtered.length} de ${rows.length} boletas · ${kpis.abiertas} abiertas · ${kpis.conciliadas} conciliadas${kpis.anuladas ? ` · ${kpis.anuladas} anuladas` : ''}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <ArrowsClockwise size={14} />
            Refrescar
          </button>
          <button onClick={exportCSV} disabled={sortedRows.length === 0} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <Download size={14} />
            Exportar CSV
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <KpiTile label="Depositado" value={formatQ(kpis.total)} tone="success" />
        <KpiTile label="Aplicado a operaciones" value={formatQ(kpis.aplicado)} />
        <KpiTile label="Disponible sin aplicar" value={formatQ(kpis.disponible)} tone={kpis.disponible > 0 ? 'danger' : undefined} />
        <KpiTile label="Boletas totales" value={String(rows.length)} />
      </div>

      {/* Por banco */}
      {porBanco.length > 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--hairline)',
          borderRadius: 12, padding: '12px 16px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
            Concentración por banco
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(porBanco.length, 4)}, 1fr)`, gap: 8 }}>
            {porBanco.slice(0, 4).map(([banco, d]) => (
              <div key={banco} style={{
                padding: '10px 12px', borderRadius: 8, background: 'var(--soft-gray)',
                borderLeft: '3px solid var(--brand)',
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink)' }}>{banco}</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{formatQ(d.total)}</div>
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                  {d.count} boleta{d.count === 1 ? '' : 's'}{d.disponible > 0.01 ? ` · Q${d.disponible.toFixed(2)} disp.` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{
        padding: '14px 16px', background: 'var(--surface)',
        border: '1px solid var(--hairline)', borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['hoy', 'ayer', '7d', '30d', 'mes'] as const).map((preset) => {
            const labels = { hoy: 'Hoy', ayer: 'Ayer', '7d': 'Últimos 7 días', '30d': 'Últimos 30 días', mes: 'Este mes' }
            const active = rangoActivo === preset
            return (
              <button
                key={preset}
                onClick={() => setRango(preset)}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  border: `1px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
                  background: active ? 'var(--brand)' : 'transparent',
                  color: active ? 'white' : 'var(--ink)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                {labels[preset]}
              </button>
            )
          })}
          <div style={{
            padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${rangoActivo === 'custom' ? 'var(--brand)' : 'var(--hairline)'}`,
            background: rangoActivo === 'custom' ? 'rgba(240,78,35,0.08)' : 'var(--soft-gray)',
            fontSize: 12, color: rangoActivo === 'custom' ? 'var(--brand)' : 'var(--muted-foreground)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ fontWeight: 600 }}>Rango</span>
            <input type="date" value={desde} onChange={(e) => setFiltro('desde', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
            <span>→</span>
            <input type="date" value={hasta} onChange={(e) => setFiltro('hasta', e.target.value)}
              style={{ fontSize: 12, height: 22, padding: '0 4px', border: 'none', background: 'transparent', color: 'inherit' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
            <MagnifyingGlass size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)' }} />
            <input type="text" value={search} onChange={(e) => setFiltro('q', e.target.value)}
              placeholder="Buscar por banco, número o cuenta…"
              style={{ fontSize: 13, height: 34, padding: '0 12px 0 32px', width: '100%' }} />
          </div>
          <div style={{ display: 'inline-flex', background: 'var(--soft-gray)', borderRadius: 8, padding: 2, gap: 2 }}>
            {[
              { v: '' as BancoEstadoFilter, label: 'Todas' },
              { v: 'abierta' as BancoEstadoFilter, label: 'Abiertas' },
              { v: 'conciliada' as BancoEstadoFilter, label: 'Conciliadas' },
              { v: 'con_disponible' as BancoEstadoFilter, label: 'Con disponible' },
              { v: 'anulada' as BancoEstadoFilter, label: 'Anuladas' },
            ].map((opt) => {
              const active = estadoFilter === opt.v
              return (
                <button key={opt.v} onClick={() => setFiltro('estado', opt.v)}
                  style={{
                    padding: '6px 12px', borderRadius: 6, border: 'none',
                    background: active ? 'var(--surface)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--muted-foreground)',
                    fontSize: 12, fontWeight: active ? 600 : 500, cursor: 'pointer',
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}>
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning size={16} weight="fill" />
          {error}
        </div>
      )}

      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '110px 160px 130px 120px 120px 120px 100px',
          gap: 12, padding: '10px 16px',
          borderBottom: '1px solid var(--hairline)', background: 'var(--soft-gray)',
        }}>
          <SortableHeader label="Fecha" sortKey="fecha" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Banco / cuenta" sortKey="banco" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Nº boleta" sortKey="numero" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Monto total" sortKey="total" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Aplicado" sortKey="aplicado" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Disponible" sortKey="disponible" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} align="right" />
          <SortableHeader label="Estado" sortKey="estado" currentSort={sortBy} currentDir={sortDir} onSort={toggleSort} />
        </div>

        {loading && sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : sortedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            Sin boletas con estos filtros.
          </div>
        ) : (
          sortedRows.map((r) => {
            const anulada = r.estado === 'anulada'
            const disponibleSignif = r.disponible > 0.01 && !anulada
            return (
              <div key={r.id} style={{
                display: 'grid',
                gridTemplateColumns: '110px 160px 130px 120px 120px 120px 100px',
                gap: 12, padding: '12px 16px',
                fontSize: 13, alignItems: 'center',
                borderBottom: '1px solid var(--hairline)',
                opacity: anulada ? 0.55 : 1,
              }}>
                <div style={{ fontSize: 12 }}>
                  {new Date(r.fecha_boleta + 'T12:00:00').toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Guatemala' })}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.banco}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)', fontFamily: 'monospace' }}>
                    {r.cuenta_alias ? `${r.cuenta_alias} · ` : ''}{r.cuenta_banco}
                  </div>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {r.numero_boleta}
                </div>
                <div style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                  {formatQ(r.monto_total)}
                </div>
                <div style={{ textAlign: 'right', fontFamily: 'monospace', color: r.monto_aplicado > 0 ? 'var(--ink)' : 'var(--muted-foreground)' }}>
                  {r.monto_aplicado > 0 ? formatQ(r.monto_aplicado) : '—'}
                </div>
                <div style={{
                  textAlign: 'right', fontFamily: 'monospace',
                  color: disponibleSignif ? 'var(--brand)' : 'var(--muted-foreground)',
                  fontWeight: disponibleSignif ? 700 : 400,
                }}>
                  {r.disponible > 0.01 ? formatQ(r.disponible) : '—'}
                </div>
                <div>
                  <span style={{
                    padding: '3px 8px', borderRadius: 999,
                    background: `${BOLETA_COLOR[r.estado]}18`,
                    color: BOLETA_COLOR[r.estado],
                    fontSize: 11, fontWeight: 600, display: 'inline-block',
                  }}>
                    {BOLETA_LABEL[r.estado]}
                  </span>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Reporte Cartera INGECOP
// ============================================================================

type CarteraTipo = 'AHORRO' | 'PF' | 'APORTACIONES'

type CarteraIngecopRow = {
  numero_cuenta: string
  agencia: string | null
  no_asociado: string | null
  nombre_asociado: string | null
  tipo_cuenta: string | null
  fecha_inicio: string | null
  fecha_final: string | null
  tasa: number | null
  acreditamiento: string | null
  saldo: number
}

const CARTERA_TIPOS: { v: CarteraTipo; label: string }[] = [
  { v: 'AHORRO', label: 'Ahorro' },
  { v: 'PF', label: 'Plazo fijo' },
  { v: 'APORTACIONES', label: 'Aportaciones' },
]

function fmtFechaCartera(f: string | null): string {
  if (!f) return ''
  const d = new Date(f.length <= 10 ? `${f}T00:00:00` : f)
  if (Number.isNaN(d.getTime())) return f
  return d.toLocaleDateString('es-GT', { timeZone: 'America/Guatemala', day: '2-digit', month: '2-digit', year: 'numeric' })
}

function CarteraIngecopSection() {
  const [tipo, setTipo] = useState<CarteraTipo>('AHORRO')
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [rows, setRows] = useState<CarteraIngecopRow[]>([])
  const [loading, setLoading] = useState(false)
  const [generado, setGenerado] = useState<CarteraTipo | null>(null)
  const [error, setError] = useState<string | null>(null)

  const esPF = generado === 'PF'

  const generar = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await supabase.rpc('reporte_cartera_ingecop', {
        p_tipo: tipo,
        p_desde: desde || null,
        p_hasta: hasta || null,
      })
      if (err) throw err
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setRows(((data ?? []) as any[]).map((r) => ({
        numero_cuenta: r.numero_cuenta ?? '',
        agencia: r.agencia ?? null,
        no_asociado: r.no_asociado ?? null,
        nombre_asociado: r.nombre_asociado ?? null,
        tipo_cuenta: r.tipo_cuenta ?? null,
        fecha_inicio: r.fecha_inicio ?? null,
        fecha_final: r.fecha_final ?? null,
        tasa: r.tasa !== null && r.tasa !== undefined ? Number(r.tasa) : null,
        acreditamiento: r.acreditamiento ?? null,
        saldo: Number(r.saldo ?? 0),
      })))
      setGenerado(tipo)
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any
      setError(err?.message ?? err?.details ?? 'Error desconocido')
      setRows([])
      setGenerado(null)
    } finally {
      setLoading(false)
    }
  }

  const totalSaldo = useMemo(() => rows.reduce((s, r) => s + r.saldo, 0), [rows])

  const headers = useMemo(() => {
    const base = ['NO', 'No. CUENTA', 'AGENCIA', 'No. ASOCIADO', 'NOMBRE DEL ASOCIADO', 'TIPO DE CUENTA', 'FECHA INICIO']
    if (esPF) base.push('FECHA FINAL')
    base.push('TASA')
    if (esPF) base.push('ACREDITAMIENTO')
    base.push('SALDO')
    return base
  }, [esPF])

  const buildFila = (r: CarteraIngecopRow, i: number): (string | number)[] => {
    const fila: (string | number)[] = [
      i + 1,
      r.numero_cuenta,
      r.agencia ?? '',
      r.no_asociado ?? '',
      r.nombre_asociado ?? '',
      r.tipo_cuenta ?? '',
      fmtFechaCartera(r.fecha_inicio),
    ]
    if (esPF) fila.push(fmtFechaCartera(r.fecha_final))
    fila.push(r.tasa !== null ? `${r.tasa}%` : '')
    if (esPF) fila.push(r.acreditamiento ?? '')
    fila.push(formatQ(r.saldo))
    return fila
  }

  const exportarXlsx = async () => {
    if (!generado) return
    const config = await fetchConfigReporte()
    const filas = rows.map((r, i) => buildFila(r, i))
    const totalRow: (string | number)[] = headers.map(() => '')
    totalRow[headers.length - 2] = 'TOTAL'
    totalRow[headers.length - 1] = formatQ(totalSaldo)
    const rango = desde || hasta ? `Del ${desde || '—'} al ${hasta || '—'}` : undefined
    await exportarExcel({
      config,
      titulo: `Cartera ${generado} · INGECOP`,
      subtitulo: rango,
      headers,
      rows: filas,
      totalRow,
      filename: `cartera-${generado.toLowerCase()}-ingecop`,
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Reporte
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>
            Cartera INGECOP
          </h2>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
            {loading ? 'Generando…' : generado ? `${rows.length} ${rows.length === 1 ? 'cuenta' : 'cuentas'} · Cartera ${generado}` : 'Elegí el tipo de cartera y generá el reporte'}
          </div>
        </div>
        <button
          onClick={exportarXlsx}
          disabled={!generado || rows.length === 0}
          className="btn btn-primary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        >
          <Download size={14} />
          Exportar Excel
        </button>
      </div>

      <div style={{
        padding: '14px 16px', background: 'var(--surface)',
        border: '1px solid var(--hairline)', borderRadius: 12,
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'inline-flex', background: 'var(--soft-gray)', borderRadius: 8, padding: 2, gap: 2 }}>
          {CARTERA_TIPOS.map((opt) => {
            const active = tipo === opt.v
            return (
              <button key={opt.v} onClick={() => setTipo(opt.v)}
                style={{
                  padding: '6px 14px', borderRadius: 6, border: 'none',
                  background: active ? 'var(--surface)' : 'transparent',
                  color: active ? 'var(--ink)' : 'var(--muted-foreground)',
                  fontSize: 12, fontWeight: active ? 600 : 500, cursor: 'pointer',
                  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                }}>
                {opt.label}
              </button>
            )
          })}
        </div>

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted-foreground)' }}>
          <span style={{ fontWeight: 600 }}>Desde</span>
          <input type="date" className="input" value={desde} onChange={(e) => setDesde(e.target.value)}
            style={{ fontSize: 12, height: 30, padding: '0 8px' }} />
          <span style={{ fontWeight: 600 }}>Hasta</span>
          <input type="date" className="input" value={hasta} onChange={(e) => setHasta(e.target.value)}
            style={{ fontSize: 12, height: 30, padding: '0 8px' }} />
        </div>

        <button
          onClick={generar}
          disabled={loading}
          className="btn btn-secondary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        >
          <ArrowsClockwise size={14} />
          {loading ? 'Generando…' : 'Generar'}
        </button>
      </div>

      {error && (
        <div className="error-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning size={16} weight="fill" />
          {error}
        </div>
      )}

      {generado && (
        <div className="card" style={{ overflow: 'auto' }}>
          {rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
              Sin cuentas en la cartera {generado} para este rango.
            </div>
          ) : (
            <table className="table" style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr>
                  {headers.map((h) => (
                    <th key={h} style={{ textAlign: h === 'TASA' || h === 'SALDO' ? 'right' : 'left' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.numero_cuenta}-${i}`}>
                    <td>{i + 1}</td>
                    <td style={{ fontFamily: 'monospace' }}>{r.numero_cuenta}</td>
                    <td>{r.agencia ?? '—'}</td>
                    <td style={{ fontFamily: 'monospace' }}>{r.no_asociado ?? '—'}</td>
                    <td>{r.nombre_asociado ?? '—'}</td>
                    <td>{r.tipo_cuenta ?? '—'}</td>
                    <td>{fmtFechaCartera(r.fecha_inicio) || '—'}</td>
                    {esPF && <td>{fmtFechaCartera(r.fecha_final) || '—'}</td>}
                    <td style={{ textAlign: 'right' }}>{r.tasa !== null ? `${r.tasa}%` : '—'}</td>
                    {esPF && <td>{r.acreditamiento ?? '—'}</td>}
                    <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{formatQ(r.saldo)}</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--soft-gray)' }}>
                  <td colSpan={headers.length - 1} style={{ textAlign: 'right', fontWeight: 700 }}>TOTAL</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{formatQ(totalSaldo)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Reporte Demográfico de asociados
// ============================================================================

type DemografiaBucket = { label: string; n: number }

type DemografiaData = {
  total_activos: number
  edad_promedio: number
  por_genero: DemografiaBucket[]
  por_edad: DemografiaBucket[]
  por_estado_civil: DemografiaBucket[]
  por_departamento: DemografiaBucket[]
  por_municipio: DemografiaBucket[]
  por_profesion: DemografiaBucket[]
  por_ingreso: DemografiaBucket[]
  por_depto_nacimiento: DemografiaBucket[]
  cobertura: { ingreso: number; nacimiento: number }
}

// Cada sección del reporte: la clave del jsonb, título visible y una nota
// opcional (cobertura para las dimensiones parciales).
type DemografiaSeccion = {
  key: keyof Pick<
    DemografiaData,
    | 'por_genero'
    | 'por_edad'
    | 'por_estado_civil'
    | 'por_departamento'
    | 'por_municipio'
    | 'por_profesion'
    | 'por_ingreso'
    | 'por_depto_nacimiento'
  >
  titulo: string
  coberturaKey?: 'ingreso' | 'nacimiento'
}

const DEMOGRAFIA_SECCIONES: DemografiaSeccion[] = [
  { key: 'por_genero', titulo: 'Género' },
  { key: 'por_edad', titulo: 'Edad (por rango)' },
  { key: 'por_estado_civil', titulo: 'Estado civil' },
  { key: 'por_departamento', titulo: 'Departamento de residencia' },
  { key: 'por_municipio', titulo: 'Municipio de residencia (top 15)' },
  { key: 'por_profesion', titulo: 'Profesión / oficio (top 15)' },
  { key: 'por_ingreso', titulo: 'Ingreso mensual aproximado', coberturaKey: 'ingreso' },
  { key: 'por_depto_nacimiento', titulo: 'Departamento de nacimiento', coberturaKey: 'nacimiento' },
]

const pct = (n: number, total: number) => (total > 0 ? (n * 100) / total : 0)

const fmtPct = (v: number) =>
  `${v.toLocaleString('es-GT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`

function DemografiaBarras({ buckets, total }: { buckets: DemografiaBucket[]; total: number }) {
  const maxN = Math.max(1, ...buckets.map((b) => b.n))
  if (buckets.length === 0) {
    return (
      <div style={{ padding: '16px 0', color: 'var(--muted-foreground)', fontSize: 13 }}>
        Sin datos para esta dimensión.
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {buckets.map((b) => {
        const ancho = (b.n / maxN) * 100
        const porcentaje = pct(b.n, total)
        return (
          <div key={b.label} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 120px', gap: 12, alignItems: 'center' }}>
            <div style={{
              fontSize: 12, color: 'var(--ink)', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }} title={b.label}>
              {b.label}
            </div>
            <div style={{
              position: 'relative', height: 20, background: 'var(--soft-gray)',
              borderRadius: 6, overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', inset: 0, width: `${ancho}%`,
                background: 'var(--brand)', borderRadius: 6,
                transition: 'width .2s',
              }} />
            </div>
            <div style={{
              fontSize: 12, textAlign: 'right', color: 'var(--ink)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              <span style={{ fontWeight: 700 }}>{b.n}</span>
              <span style={{ color: 'var(--muted-foreground)', marginLeft: 6 }}>{fmtPct(porcentaje)}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function DemografiaReporte() {
  const [data, setData] = useState<DemografiaData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: res, error: err } = await supabase.rpc('reporte_demografico_asociados', {
        p_desde: desde || null,
        p_hasta: hasta || null,
      })
      if (err) throw err
      setData(res as DemografiaData)
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errAny = e as any
      const msg = errAny?.message ?? errAny?.details ?? errAny?.hint ?? JSON.stringify(errAny) ?? 'Error desconocido'
      setError(msg)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [desde, hasta])

  const limpiarFechas = useCallback(async () => {
    setDesde(''); setHasta('')
    setLoading(true); setError(null)
    try {
      const { data: res, error: err } = await supabase.rpc('reporte_demografico_asociados', { p_desde: null, p_hasta: null })
      if (err) throw err
      setData(res as DemografiaData)
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setError((e as any)?.message ?? 'Error'); setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Carga inicial (sin filtro). Los cambios de fecha se aplican con el botón.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load() }, [])

  const total = data?.total_activos ?? 0

  const kpis = useMemo(() => {
    if (!data) return { total: 0, edad: 0, pctH: 0, pctM: 0 }
    const genero = data.por_genero ?? []
    const norm = (s: string) => s.trim().toUpperCase()
    const h = genero.find((g) => norm(g.label).startsWith('M') && !norm(g.label).startsWith('F'))?.n
      ?? genero.find((g) => norm(g.label).includes('MASC'))?.n ?? 0
    const m = genero.find((g) => norm(g.label).startsWith('F'))?.n
      ?? genero.find((g) => norm(g.label).includes('FEM'))?.n ?? 0
    return {
      total: data.total_activos,
      edad: data.edad_promedio,
      pctH: pct(h, data.total_activos),
      pctM: pct(m, data.total_activos),
    }
  }, [data])

  // Construye una tabla plana para el export: [Dimensión, Categoría, Conteo, % del total]
  const buildExportRows = (): (string | number)[][] => {
    if (!data) return []
    const rows: (string | number)[][] = []
    for (const sec of DEMOGRAFIA_SECCIONES) {
      const buckets = data[sec.key] ?? []
      for (const b of buckets) {
        rows.push([sec.titulo, b.label, b.n, fmtPct(pct(b.n, total))])
      }
    }
    return rows
  }

  const rangoTexto = desde || hasta ? ` · Ingreso ${desde || '—'} a ${hasta || 'hoy'}` : ''
  const subtitulo = data
    ? `${data.total_activos} asociados activos${rangoTexto} · Edad promedio ${data.edad_promedio.toLocaleString('es-GT', { maximumFractionDigits: 1 })} años · Generado ${new Date().toLocaleDateString('es-GT', { timeZone: 'America/Guatemala' })}`
    : undefined

  const exportarXlsx = async () => {
    if (!data) return
    const config = await fetchConfigReporte()
    await exportarExcel({
      config,
      titulo: 'Reporte demográfico de asociados',
      subtitulo,
      headers: ['Dimensión', 'Categoría', 'Conteo', '% del total'],
      rows: buildExportRows(),
      filename: 'reporte-demografico-asociados',
    })
  }

  const exportarPdf = async () => {
    if (!data) return
    const config = await fetchConfigReporte()
    await exportarPDF({
      config,
      titulo: 'Reporte demográfico de asociados',
      subtitulo,
      headers: ['Dimensión', 'Categoría', 'Conteo', '% del total'],
      rows: buildExportRows(),
      columnasNumericas: [2, 3],
      filename: 'reporte-demografico-asociados',
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Reporte
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>
            Demografía de asociados
          </h2>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
            {loading ? 'Cargando…' : data ? `Perfil de ${data.total_activos} asociados activos` : 'Sin datos'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <ArrowsClockwise size={14} />
            Refrescar
          </button>
          <button onClick={exportarPdf} disabled={!data} className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <Download size={14} />
            Exportar PDF
          </button>
          <button onClick={exportarXlsx} disabled={!data} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <Download size={14} />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Filtro por fecha de ingreso del asociado */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap',
        background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, padding: '12px 14px',
      }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)' }}>
          Desde (fecha de ingreso)
          <input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)' }}>
          Hasta
          <input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
        </label>
        <button onClick={load} className="btn btn-primary" style={{ fontSize: 13 }}>Aplicar</button>
        {(desde || hasta) && (
          <button onClick={limpiarFechas} className="btn" style={{ fontSize: 13 }}>Limpiar</button>
        )}
        <span style={{ fontSize: 12, color: 'var(--muted-foreground)', marginLeft: 'auto' }}>
          {desde || hasta
            ? `Filtrado: ${desde || '—'} a ${hasta || 'hoy'}`
            : 'Todos los asociados activos'}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <KpiTile label="Asociados activos" value={loading ? '…' : String(kpis.total)} />
        <KpiTile label="Edad promedio" value={loading ? '…' : `${kpis.edad.toLocaleString('es-GT', { maximumFractionDigits: 1 })} años`} />
        <KpiTile label="Hombres" value={loading ? '…' : fmtPct(kpis.pctH)} />
        <KpiTile label="Mujeres" value={loading ? '…' : fmtPct(kpis.pctM)} tone="success" />
      </div>

      {error && (
        <div className="error-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning size={16} weight="fill" />
          {error}
        </div>
      )}

      {loading && !data ? (
        <div style={{
          padding: 40, textAlign: 'center', color: 'var(--muted-foreground)',
          background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12,
        }}>
          Cargando…
        </div>
      ) : data ? (
        DEMOGRAFIA_SECCIONES.map((sec) => {
          const buckets = data[sec.key] ?? []
          const cobertura = sec.coberturaKey ? data.cobertura?.[sec.coberturaKey] : undefined
          return (
            <div key={sec.key} style={{
              padding: '16px 18px', background: 'var(--surface)',
              border: '1px solid var(--hairline)', borderRadius: 12,
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                  {sec.titulo}
                </h3>
                {cobertura !== undefined && (
                  <span style={{
                    fontSize: 11, color: 'var(--muted-foreground)',
                    background: 'var(--soft-gray)', padding: '3px 10px', borderRadius: 999,
                  }}>
                    Basado en {cobertura.toLocaleString('es-GT', { maximumFractionDigits: 0 })}% de los asociados
                  </span>
                )}
              </div>
              <DemografiaBarras buckets={buckets} total={total} />
            </div>
          )
        })
      ) : null}
    </div>
  )
}

